import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  lazy,
  Suspense
} from 'react';
import { createPortal } from 'react-dom';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Alert from './caseTable/Alert';
import { SlNote } from "react-icons/sl";
import {
  FiLock,
  FiGlobe,
  FiEye,
  FiEyeOff,
  FiUser,
  FiCalendar,
  FiPlus,
  FiMessageSquare,
  FiShield,
  FiFileText,
  FiClock,
  FiAtSign,
  FiPaperclip,
  FiX,
  FiDownload,
  // ✅ NEW (notes board + user filter)
  FiSearch,
  FiChevronDown,
  FiUsers,
  FiFilter,
  FiColumns,
  FiEdit3,
  // ✅ NEW (prominent toast)
  FiCheckCircle,
  FiAlertCircle,
  FiAlertTriangle,
  FiInfo
} from "react-icons/fi";
import DOMPurify from 'dompurify';
import Spinner from 'react-bootstrap/Spinner';
import Task from './caseTable/Task';
import CaseTable from './caseTable/CaseTable';
import Modal from 'react-modal';
import UserMentionPicker from '../../../../mention/UserMentionPicker';
// ✅ NEW: emoji reactions on notes (only mentioned users can react)
import NoteReactions from '../../../../../pages/NoteReactions';

const ReactQuill = lazy(() =>
  Promise.all([
    import('react-quill'),
    import('react-quill/dist/quill.snow.css')
  ]).then(([mod]) => mod)
);

Modal.setAppElement('#root');

const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

/* ============================================================
   ✅ NEW: screenshot from the STL viewer -> Add Note modal
   (event names must match caseTable/components/ScreenshotEditor.js)
   ============================================================ */
const STL_SHOT_PROBE_EVENT = 'stl-screenshot:can-add-note';
const STL_SHOT_ADD_NOTE_EVENT = 'stl-screenshot:add-note';
const NOTE_MODAL_ON_TOP_Z = 12500; // above the full-screen 3D viewer

/* ============================================================
   ✅ NEW: note editing (author only, within 7 minutes)
   The server enforces the same rule - this is only for the UI.
   ============================================================ */
const NOTE_EDIT_WINDOW_MS = 7 * 60 * 1000;

const getEditTimeLeft = (createdAt) => {
  if (!createdAt) return 0;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return 0;
  return Math.max(0, created + NOTE_EDIT_WINDOW_MS - Date.now());
};

const formatTimeLeft = (ms) => {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// live countdown (ticks every second only while the note is still editable)
const useEditTimeLeft = (createdAt, enabled) => {
  const [left, setLeft] = useState(() => (enabled ? getEditTimeLeft(createdAt) : 0));

  useEffect(() => {
    if (!enabled) {
      setLeft(0);
      return undefined;
    }

    const first = getEditTimeLeft(createdAt);
    setLeft(first);
    if (first <= 0) return undefined;

    const t = setInterval(() => {
      const l = getEditTimeLeft(createdAt);
      setLeft(l);
      if (l <= 0) clearInterval(t);
    }, 1000);

    return () => clearInterval(t);
  }, [createdAt, enabled]);

  return left;
};

// "From: ... Created: ..." header that is stored in front of every note
const buildNoteHeader = ({ userName, userRole, recipient, subject }) => `
        <div style="
          font-weight: bold;
          margin-bottom: 15px;
          border-bottom: 1px solid #eee;
          padding-bottom: 5px;
        ">
          From: ${userName || recipient || 'Unknown User'}
          ${userRole ? `(${userRole})` : ''}
          <br/>

          Created:
          ${new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}

          ${subject ? ` for ${subject}` : ''}
        </div>

        `;

// the editable part of a note (without the From/Created header)
const getNoteBody = (note) => {
  if (!note) return '';
  if (typeof note.bodyHtml === 'string') return note.bodyHtml;
  // older notes: drop the first header <div>…</div>
  return (note.text || '').replace(/^\s*<div[^>]*>[\s\S]*?<\/div>\s*/, '');
};

// selector for the (hidden) search box inside UserMentionPicker
const PICKER_SEARCH_SELECTOR =
  'input[type="text"], input[type="search"], input:not([type])';

const QUILL_MODULES = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ size: [] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ color: [] }, { background: [] }],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' }
    ],
    [{ align: [] }],
    ['link', 'image'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const QUILL_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'color',
  'background',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'align'
];

const stripHtml = (html) =>
  html
    ? html.replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, ' ').trim()
    : '';

const toBool = (v) => v === true || v === 'true' || v === 1 || v === '1';

const sortByCreatedAt = (arr) =>
  arr.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const isImage = (mimetype) => (mimetype || '').startsWith('image/');

const getAttachmentUrl = (att) => {
  if (!att) return '';
  if (att.url) {
    return att.url.startsWith('http') ? att.url : `${API_BASE_URL}${att.url}`;
  }
  if (att.path) {
    return `${API_BASE_URL}/uploads/notes/${att.name || ''}`;
  }
  return '';
};

// Clipboard screenshots usually come as a generic "image.png" - give them a unique name
let pastedCounter = 0;
const normalizePastedFile = (f) => {
  const generic = !f.name || /^image\.(png|jpe?g|gif|webp|bmp)$/i.test(f.name);
  if (!generic) return f;

  pastedCounter += 1;
  const ext = ((f.type || 'image/png').split('/')[1] || 'png')
    .replace('jpeg', 'jpg')
    .replace('svg+xml', 'svg');

  return new File([f], `pasted-${Date.now()}-${pastedCounter}.${ext}`, {
    type: f.type,
    lastModified: f.lastModified || Date.now()
  });
};

/* ============================================================
   ✅ NEW: helpers for the user filter
   ============================================================ */
const normName = (s) => (s || '').toString().trim().toLowerCase();

const IGNORED_NAMES = new Set(['', 'unknown', 'unknown user']);

const getInitials = (name) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('') || '?';

const AVATAR_COLORS = [
  '#0066cc',
  '#17a2b8',
  '#6f42c1',
  '#e83e8c',
  '#fd7e14',
  '#20a779',
  '#6610f2',
  '#c0392b'
];

const avatarColor = (key) => {
  let h = 0;
  for (const c of key || '') h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// does this note belong to the selected user? (written by and/or mentions them)
const noteMatchesUser = (note, opt, mode) => {
  if (!opt) return true;

  const authored =
    normName(note.createdBy) === opt.key ||
    (note.authorUserId !== undefined &&
      note.authorUserId !== null &&
      opt.ids.has(String(note.authorUserId)));

  const mentioned =
    Array.isArray(note.mentions) &&
    note.mentions.some(
      (m) =>
        m &&
        (normName(m.userName) === opt.key ||
          (m.userId !== undefined &&
            m.userId !== null &&
            opt.ids.has(String(m.userId))))
    );

  if (mode === 'authored') return authored;
  if (mode === 'mentioned') return mentioned;
  return authored || mentioned;
};

/* Preview of a selected (not yet uploaded) file - shown INSIDE the editor box */
const FilePreview = ({ file, onRemove }) => {
  const [url, setUrl] = useState('');
  const image = isImage(file?.type);

  useEffect(() => {
    if (!file || !isImage(file.type)) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  return (
    <div style={styles.previewItem}>
      {image && url ? (
        <img src={url} alt={file.name} style={styles.previewImg} />
      ) : (
        <div style={styles.previewFileBox}>
          <FiFileText size={22} color="#0066cc" />
          <span style={styles.previewFileName} title={file.name}>
            {file.name}
          </span>
          <span style={styles.previewFileSize}>
            {formatFileSize(file.size)}
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={onRemove}
        style={styles.previewRemove}
        title="Remove"
      >
        <FiX size={12} />
      </button>
    </div>
  );
};

/* ============================================================
   ✅ NEW: USER FILTER (searchable dropdown)
   ============================================================ */
const UserFilter = React.memo(function UserFilter({ options, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = normName(query);
    return q ? options.filter((o) => o.key.includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    setActive(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;

    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (opt) => {
    onSelect(opt ? opt.key : null);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[active]) {
        e.preventDefault();
        pick(filtered[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={wrapRef} className="cn-user-filter">
      <div
        className={`cn-user-input ${open ? 'is-open' : ''}`}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {selected && !open ? (
          <span
            className="cn-avatar sm"
            style={{ background: avatarColor(selected.key) }}
          >
            {getInitials(selected.name)}
          </span>
        ) : (
          <FiSearch size={15} className="cn-muted" />
        )}

        <input
          ref={inputRef}
          value={open ? query : selected?.name || ''}
          placeholder={selected ? selected.name : 'Search user…'}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          aria-label="Filter notes by user"
        />

        {selected && (
          <button
            type="button"
            className="cn-clear"
            title="Clear filter"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              pick(null);
            }}
          >
            <FiX size={12} />
          </button>
        )}

        <FiChevronDown
          size={15}
          className="cn-muted"
          style={{
            transition: 'transform .15s',
            transform: open ? 'rotate(180deg)' : 'none'
          }}
        />
      </div>

      {open && (
        <div className="cn-user-menu" role="listbox">
          {!query && (
            <button
              type="button"
              className={`cn-user-opt ${!selected ? 'is-selected' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(null)}
            >
              <span className="cn-avatar all">
                <FiUsers size={13} />
              </span>
              <span className="cn-user-opt-text">
                <span className="cn-user-opt-name">All users</span>
                <span className="cn-user-opt-meta">Show every note</span>
              </span>
            </button>
          )}

          {filtered.length === 0 ? (
            <div className="cn-user-empty">
              {options.length === 0
                ? 'No users found in these notes'
                : `No users match "${query}"`}
            </div>
          ) : (
            filtered.map((o, i) => (
              <button
                key={o.key}
                type="button"
                role="option"
                aria-selected={selected?.key === o.key}
                className={`cn-user-opt ${i === active ? 'is-active' : ''} ${
                  selected?.key === o.key ? 'is-selected' : ''
                }`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}
              >
                <span
                  className="cn-avatar"
                  style={{ background: avatarColor(o.key) }}
                >
                  {getInitials(o.name)}
                </span>
                <span className="cn-user-opt-text">
                  <span className="cn-user-opt-name">{o.name}</span>
                  <span className="cn-user-opt-meta">
                    {o.authored} written · {o.mentioned} mentioned
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
});

/* ============================================================
   ✅ NEW: IN-APP IMAGE VIEWER for note attachments
   - opens inside the software (no new browser tab)
   - big image with zoom (buttons / wheel / double-click) + drag to pan
   - previous / next when the note has several images (also ← → keys)
   - right side: who wrote the note, when, type, mentions and the note text
   ============================================================ */
const NIV_CSS = `
  .niv-overlay {
    position: fixed; inset: 0; z-index: 12800;
    background: rgba(6, 12, 26, 0.82);
    display: flex; align-items: center; justify-content: center;
    padding: 16px; box-sizing: border-box;
    animation: niv-fade .18s ease-out;
  }
  @keyframes niv-fade { from { opacity: 0; } to { opacity: 1; } }
  .niv-panel {
    width: min(1280px, 100%); height: min(860px, 100%);
    display: flex; flex-direction: column;
    background: #fff; border-radius: 12px; overflow: hidden;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5);
    animation: niv-in .22s ease-out;
  }
  @keyframes niv-in { from { opacity: 0; transform: translateY(12px) scale(.98); } to { opacity: 1; transform: none; } }

  .niv-head {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 14px; flex-shrink: 0; color: #fff;
    background: linear-gradient(115deg, #004aad 0%, #003b88 100%);
  }
  .niv-head-icon {
    width: 36px; height: 36px; flex: 0 0 auto; border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.14); border: 1px solid rgba(255,255,255,0.22);
  }
  .niv-title { font-size: 15px; font-weight: 700; line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .niv-sub { font-size: 11px; opacity: .78; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .niv-head-actions { margin-left: auto; display: flex; align-items: center; gap: 6px; flex: 0 0 auto; }
  .niv-hbtn {
    width: 34px; height: 34px; border-radius: 8px; padding: 0;
    border: 1px solid rgba(255,255,255,0.22); background: rgba(255,255,255,0.12);
    color: #fff; display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; text-decoration: none; transition: background .15s;
  }
  .niv-hbtn:hover { background: rgba(255,255,255,0.28); color: #fff; text-decoration: none; }

  .niv-body { flex: 1 1 auto; min-height: 0; display: flex; }

  .niv-stage-wrap { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; background: #111827; }
  .niv-stage {
    position: relative; flex: 1 1 auto; min-height: 0; overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    touch-action: none; user-select: none;
    background:
      linear-gradient(45deg, #161f30 25%, transparent 25%) 0 0 / 22px 22px,
      linear-gradient(-45deg, #161f30 25%, transparent 25%) 0 0 / 22px 22px,
      #111827;
  }
  .niv-img {
    max-width: 100%; max-height: 100%; object-fit: contain;
    transform-origin: center center; will-change: transform;
    box-shadow: 0 6px 28px rgba(0,0,0,0.45);
    -webkit-user-drag: none;
  }
  .niv-nav {
    position: absolute; top: 50%; transform: translateY(-50%); z-index: 2;
    width: 42px; height: 42px; border-radius: 50%; padding: 0;
    border: 1px solid rgba(255,255,255,0.35); background: rgba(15,23,42,0.65);
    color: #fff; display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: background .15s;
  }
  .niv-nav:hover { background: rgba(0,74,173,0.9); }
  .niv-nav.prev { left: 12px; }
  .niv-nav.next { right: 12px; }
  .niv-counter {
    position: absolute; top: 12px; left: 50%; transform: translateX(-50%); z-index: 2;
    padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 700;
    background: rgba(15,23,42,0.7); color: #fff;
  }

  .niv-toolbar {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    padding: 8px; flex-shrink: 0; background: #0b1220; color: #cbd5e1;
  }
  .niv-tbtn {
    min-width: 32px; height: 30px; border-radius: 7px; padding: 0 8px;
    border: 1px solid #334155; background: #1e293b; color: #e2e8f0;
    font-size: 12px; font-weight: 600; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center; gap: 4px;
  }
  .niv-tbtn:hover:not(:disabled) { background: #004aad; border-color: #004aad; }
  .niv-tbtn:disabled { opacity: .4; cursor: not-allowed; }
  .niv-zoom { min-width: 52px; text-align: center; font-size: 12px; font-variant-numeric: tabular-nums; }

  .niv-thumbs {
    display: flex; gap: 8px; padding: 8px 10px; overflow-x: auto; flex-shrink: 0;
    background: #0b1220; border-top: 1px solid #1e293b;
  }
  .niv-thumb {
    width: 64px; height: 48px; flex: 0 0 auto; border-radius: 6px; overflow: hidden;
    border: 2px solid transparent; padding: 0; background: #1e293b; cursor: pointer;
    opacity: .65; transition: opacity .15s, border-color .15s;
  }
  .niv-thumb:hover { opacity: 1; }
  .niv-thumb.is-on { border-color: #3b82f6; opacity: 1; }
  .niv-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

  .niv-side {
    width: 360px; flex: 0 0 360px; min-height: 0; overflow-y: auto;
    border-left: 1px solid #e9ecef; background: #f8fafc;
    display: flex; flex-direction: column;
  }
  .niv-sec { padding: 14px 16px; border-bottom: 1px solid #e9ecef; }
  .niv-sec:last-child { border-bottom: none; }
  .niv-label {
    font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px;
    color: #64748b; margin-bottom: 8px; display: flex; align-items: center; gap: 5px;
  }
  .niv-author { display: flex; align-items: center; gap: 10px; }
  .niv-avatar {
    width: 38px; height: 38px; border-radius: 50%; flex: 0 0 auto;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 13px; font-weight: 700;
  }
  .niv-author-name { font-size: 14px; font-weight: 700; color: #0f172a; }
  .niv-author-meta { font-size: 12px; color: #64748b; margin-top: 2px; }
  .niv-badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
  .niv-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 9px; border-radius: 12px; font-size: 10.5px; font-weight: 700;
    text-transform: uppercase; letter-spacing: .3px;
  }
  .niv-badge.int { background: #fff3cd; color: #856404; }
  .niv-badge.ext { background: #d1ecf1; color: #0c5460; }
  .niv-badge.edited { background: #f1f5f9; color: #64748b; text-transform: none; font-style: italic; }
  .niv-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .niv-chip {
    background: #e8f4f8; color: #0056b3; border: 1px solid #b8daff;
    border-radius: 12px; padding: 2px 9px; font-size: 12px; font-weight: 600;
  }
  .niv-text {
    font-size: 14px; color: #1e293b; line-height: 1.6;
    background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px;
    word-break: break-word; overflow-wrap: anywhere;
  }
  .niv-text p { margin: 0 0 8px; }
  .niv-text p:last-child { margin-bottom: 0; }
  .niv-text img { max-width: 100%; height: auto; }
  .niv-empty { color: #94a3b8; font-style: italic; font-size: 13px; }
  .niv-file {
    display: flex; align-items: center; gap: 8px; padding: 7px 9px;
    background: #fff; border: 1px solid #e2e8f0; border-radius: 7px;
    font-size: 12px; color: #1e293b; text-decoration: none; margin-bottom: 6px;
  }
  .niv-file:hover { border-color: #93c5fd; background: #eff6ff; color: #1e293b; text-decoration: none; }
  .niv-file-name { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 600; }
  .niv-file-size { color: #94a3b8; font-size: 11px; flex: 0 0 auto; }

  @media (max-width: 900px) {
    .niv-side { width: 300px; flex-basis: 300px; }
  }
  @media (max-width: 767px) {
    .niv-overlay { padding: 0; }
    .niv-panel { width: 100%; height: 100%; border-radius: 0; }
    .niv-body { flex-direction: column; }
    .niv-stage-wrap { flex: 0 0 55%; }
    .niv-side { width: 100%; flex: 1 1 auto; border-left: none; border-top: 1px solid #e9ecef; }
    .niv-head { padding: 10px; }
    .niv-sub { display: none; }
    .niv-nav { width: 36px; height: 36px; }
    .niv-thumb { width: 52px; height: 40px; }
    .niv-sec { padding: 12px; }
    .niv-text { font-size: 13px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .niv-overlay, .niv-panel { animation: none; }
  }
`;

const NIV_MIN_ZOOM = 1;
const NIV_MAX_ZOOM = 6;

const NivIcon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);

const NoteImageViewer = ({ note, startAttId, onClose }) => {
  const images = useMemo(
    () =>
      (Array.isArray(note?.attachments) ? note.attachments : []).filter((a) =>
        isImage(a?.mimetype)
      ),
    [note]
  );
  const others = useMemo(
    () =>
      (Array.isArray(note?.attachments) ? note.attachments : []).filter(
        (a) => a && !isImage(a.mimetype)
      ),
    [note]
  );

  const [index, setIndex] = useState(() => {
    const i = images.findIndex((a) => (a._id || a.name) === startAttId);
    return i >= 0 ? i : 0;
  });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  const count = images.length;
  const current = images[Math.min(index, Math.max(count - 1, 0))];
  const src = current ? getAttachmentUrl(current) : '';
  const name = current ? current.originalName || current.name || 'Image' : '';

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const go = useCallback(
    (step) => {
      if (count < 2) return;
      setIndex((i) => (i + step + count) % count);
      resetView();
    },
    [count, resetView]
  );

  const zoomBy = useCallback((factor) => {
    setZoom((z) => {
      const next = Math.min(NIV_MAX_ZOOM, Math.max(NIV_MIN_ZOOM, +(z * factor).toFixed(2)));
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // lock page scroll + keyboard (Esc closes only this viewer)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === '+' || e.key === '=') {
        zoomBy(1.25);
      } else if (e.key === '-') {
        zoomBy(0.8);
      } else if (e.key === '0') {
        resetView();
      }
    };

    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prev;
    };
  }, [onClose, go, zoomBy, resetView]);

  const onWheel = (e) => {
    zoomBy(e.deltaY < 0 ? 1.12 : 0.89);
  };

  const onPointerDown = (e) => {
    if (zoom <= 1 || e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setPan({ x: d.px + (e.clientX - d.sx), y: d.py + (e.clientY - d.sy) });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const onDoubleClick = () => {
    if (zoom > 1) resetView();
    else setZoom(2.5);
  };

  if (!note || !current) return null;

  const isInternal = toBool(note.isInternal);
  const author = note.createdBy || 'Unknown';
  const created = note.createdAt
    ? new Date(note.createdAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'Unknown date';

  const bodyHtml = DOMPurify.sanitize(getNoteBody(note) || '');
  const hasText = !!stripHtml(bodyHtml);
  const mentions = Array.isArray(note.mentions) ? note.mentions.filter(Boolean) : [];

  return createPortal(
    <div
      className="niv-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
    >
      <style>{NIV_CSS}</style>

      <div className="niv-panel">
        {/* header */}
        <div className="niv-head">
          <span className="niv-head-icon">
            <NivIcon
              size={17}
              d={
                <>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </>
              }
            />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="niv-title" title={name}>{name}</div>
            <div className="niv-sub">
              {formatFileSize(current.size)} · Note by {author} · {created}
            </div>
          </div>

          <div className="niv-head-actions">
            <a
              className="niv-hbtn"
              href={src}
              download={name}
              title="Download"
              aria-label="Download image"
            >
              <FiDownload size={16} />
            </a>
            <button type="button" className="niv-hbtn" onClick={onClose} title="Close (Esc)" aria-label="Close">
              <FiX size={18} />
            </button>
          </div>
        </div>

        <div className="niv-body">
          {/* image */}
          <div className="niv-stage-wrap">
            <div
              className="niv-stage"
              onWheel={onWheel}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onDoubleClick={onDoubleClick}
              style={{ cursor: zoom > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'zoom-in' }}
            >
              {count > 1 && (
                <span className="niv-counter">
                  {index + 1} / {count}
                </span>
              )}

              {count > 1 && (
                <button
                  type="button"
                  className="niv-nav prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    go(-1);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  title="Previous (←)"
                  aria-label="Previous image"
                >
                  <NivIcon d={<polyline points="15 18 9 12 15 6" />} size={20} />
                </button>
              )}

              <img
                key={src}
                src={src}
                alt={name}
                className="niv-img"
                draggable={false}
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transition: dragRef.current ? 'none' : 'transform .12s ease-out'
                }}
              />

              {count > 1 && (
                <button
                  type="button"
                  className="niv-nav next"
                  onClick={(e) => {
                    e.stopPropagation();
                    go(1);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  title="Next (→)"
                  aria-label="Next image"
                >
                  <NivIcon d={<polyline points="9 18 15 12 9 6" />} size={20} />
                </button>
              )}
            </div>

            <div className="niv-toolbar">
              <button type="button" className="niv-tbtn" onClick={() => zoomBy(0.8)} disabled={zoom <= NIV_MIN_ZOOM} title="Zoom out (-)">
                −
              </button>
              <span className="niv-zoom">{Math.round(zoom * 100)}%</span>
              <button type="button" className="niv-tbtn" onClick={() => zoomBy(1.25)} disabled={zoom >= NIV_MAX_ZOOM} title="Zoom in (+)">
                +
              </button>
              <button type="button" className="niv-tbtn" onClick={resetView} disabled={zoom === 1 && pan.x === 0 && pan.y === 0} title="Fit (0)">
                Fit
              </button>
            </div>

            {count > 1 && (
              <div className="niv-thumbs">
                {images.map((a, i) => (
                  <button
                    key={a._id || a.name}
                    type="button"
                    className={`niv-thumb${i === index ? ' is-on' : ''}`}
                    onClick={() => {
                      setIndex(i);
                      resetView();
                    }}
                    title={a.originalName || a.name}
                  >
                    <img src={getAttachmentUrl(a)} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* note details */}
          <aside className="niv-side">
            <div className="niv-sec">
              <div className="niv-label">
                <FiUser size={11} /> Note by
              </div>
              <div className="niv-author">
                <span className="niv-avatar" style={{ background: avatarColor(normName(author)) }}>
                  {getInitials(author)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="niv-author-name">{author}</div>
                  <div className="niv-author-meta">
                    <FiCalendar size={11} /> {created}
                  </div>
                </div>
              </div>
              <div className="niv-badges">
                <span className={`niv-badge ${isInternal ? 'int' : 'ext'}`}>
                  {isInternal ? <FiLock size={10} /> : <FiGlobe size={10} />}
                  {isInternal ? 'Internal' : 'External'}
                </span>
                {note.subject && <span className="niv-badge ext" style={{ background: '#eef2ff', color: '#3730a3' }}>{note.subject}</span>}
                {note.isEdited && (
                  <span className="niv-badge edited">
                    <FiEdit3 size={10} /> edited
                  </span>
                )}
              </div>
            </div>

            {mentions.length > 0 && (
              <div className="niv-sec">
                <div className="niv-label">
                  <FiAtSign size={11} /> Mentioned
                </div>
                <div className="niv-chips">
                  {mentions.map((m) => (
                    <span key={m.userId || m.userName} className="niv-chip">
                      @{m.userName}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="niv-sec">
              <div className="niv-label">
                <FiMessageSquare size={11} /> Note
              </div>
              {hasText ? (
                <div className="niv-text" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
              ) : (
                <div className="niv-empty">This note has no text.</div>
              )}
            </div>

            {others.length > 0 && (
              <div className="niv-sec">
                <div className="niv-label">
                  <FiPaperclip size={11} /> Other files ({others.length})
                </div>
                {others.map((a) => {
                  const href = getAttachmentUrl(a);
                  const n = a.originalName || a.name;
                  return (
                    <a key={a._id || a.name} className="niv-file" href={href} download={n} title={`Download ${n}`}>
                      <FiFileText size={15} color="#0066cc" />
                      <span className="niv-file-name">{n}</span>
                      <span className="niv-file-size">{formatFileSize(a.size)}</span>
                      <FiDownload size={13} color="#0066cc" />
                    </a>
                  );
                })}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ============================================================
   NOTE ITEM (memoized - only re-renders when its own note changes)
   ============================================================ */
const NoteItem = React.memo(function NoteItem({
  note,
  isAdmin,
  isMentionedForMe,
  onToggleVisibility,
  onToggleInternal,
  caseId,              // ✅ NEW
  myUserIdNum,         // ✅ NEW
  onReactionsUpdated,  // ✅ NEW
  isMine = false,      // ✅ NEW: current user wrote this note
  onEdit,              // ✅ NEW: open the edit modal
  onOpenImage          // ✅ NEW: open an image inside the app (not a new tab)
}) {
  const isInternal = toBool(note.isInternal);
  const isHidden = toBool(note.isHidden);

  // ✅ NEW: author can edit for 7 minutes after posting
  const editTimeLeft = useEditTimeLeft(note.createdAt, isMine && !!onEdit);
  const canEdit = isMine && !!onEdit && editTimeLeft > 0;

  const hiddenTime = note.hiddenAt
    ? new Date(note.hiddenAt).toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'Unknown';

  const shouldBlur = isHidden && !isAdmin;

  const showThisInternalNote = isInternal
    ? note.showInternalContent !== false
    : true;

  const safeHtml = useMemo(
    () => DOMPurify.sanitize(note.text || ''),
    [note.text]
  );

  return (
    <div
      id={note._id}
      className="cn-note"
      style={{
        ...styles.noteItem,
        borderLeftColor: isMentionedForMe
          ? '#ffc107'
          : isInternal
          ? '#ffc107'
          : '#17a2b8',
        background: isHidden
          ? '#fff9f9'
          : isMentionedForMe
          ? '#fffbe6'
          : 'white',
        ...(isHidden && { borderLeftColor: '#dc3545' }),
        ...(isMentionedForMe && styles.noteItemMentioned)
      }}
    >
      {isAdmin && isHidden && (
        <div style={styles.adminOverlay}>
          <FiEyeOff size={10} />
          Hidden from users
        </div>
      )}

      {isMentionedForMe && (
        <div style={styles.mentionBadge} className="cn-mention-badge">
          <FiAtSign size={11} /> Mentioned you
        </div>
      )}

      <div style={styles.noteHeader} className="cn-note-header">
        {isInternal && (
          <label
            style={styles.internalToggleCheckbox}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={showThisInternalNote}
              onChange={(e) => {
                e.stopPropagation();
                onToggleInternal(note._id, e);
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <span>Show</span>
          </label>
        )}

        <span
          style={{
            ...styles.noteBadge,
            ...(isInternal ? styles.internalBadge : styles.externalBadge)
          }}
        >
          {isInternal ? <FiLock size={11} /> : <FiGlobe size={11} />}
          {isInternal ? 'Internal' : 'External'}
        </span>

        {isHidden && (
          <span style={{ ...styles.noteBadge, ...styles.hiddenBadge }}>
            <FiEyeOff size={11} />
            Hidden
          </span>
        )}

        {isAdmin && (
          <span style={{ ...styles.noteBadge, ...styles.adminBadge }}>
            <FiShield size={11} />
            Admin
          </span>
        )}

        <span style={styles.separator}>•</span>

        <span>
          <FiUser size={12} /> {note.createdBy || 'Unknown'}
        </span>

        {note.createdByUserId && (
          <>
            <span style={styles.separator}>•</span>
            <span style={{ fontSize: '11px', color: '#adb5bd' }}>
              ID: {note.createdByUserId}
            </span>
          </>
        )}

        <span style={styles.separator}>•</span>

        <span>
          <FiCalendar size={12} />{' '}
          {note.createdAt
            ? new Date(note.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })
            : 'Unknown'}
        </span>

        <span style={styles.separator}>•</span>

        <span style={{ fontSize: '12px' }}>
          {note.createdAt
            ? new Date(note.createdAt).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })
            : ''}
        </span>

        {/* ✅ NEW: "edited" marker */}
        {note.isEdited && (
          <>
            <span style={styles.separator}>•</span>
            <span
              style={styles.editedTag}
              title={
                note.editedAt
                  ? `Edited ${new Date(note.editedAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}`
                  : 'Edited'
              }
            >
              <FiEdit3 size={10} /> edited
            </span>
          </>
        )}

        {isHidden && note.hiddenBy && (
          <>
            <span style={styles.separator}>•</span>
            <span
              style={{
                fontSize: '11px',
                color: '#dc3545',
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}
            >
              <FiEyeOff size={10} />
              Hidden by: {note.hiddenBy}
            </span>
          </>
        )}

        {isHidden && note.hiddenAt && (
          <>
            <span style={styles.separator}>•</span>
            <span
              style={{
                fontSize: '11px',
                color: '#dc3545',
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}
            >
              <FiClock size={10} />
              {hiddenTime}
            </span>
          </>
        )}
      </div>

      {Array.isArray(note.mentions) && note.mentions.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginBottom: '8px',
            fontSize: '12px',
            alignItems: 'center'
          }}
        >
          <span
            style={{
              color: '#6c757d',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <FiAtSign size={12} /> Mentioned:
          </span>
          {note.mentions.map((m) => (
            <span
              key={m.userId || m.userName}
              className="cn-mention-chip"
              style={{
                background: '#e8f4f8',
                color: '#0056b3',
                borderRadius: '12px',
                padding: '2px 8px',
                fontWeight: 600,
                border: '1px solid #b8daff'
              }}
            >
              @{m.userName}
            </span>
          ))}
        </div>
      )}

      <div style={styles.noteContent} className="cn-note-content">
        {isInternal && !showThisInternalNote ? (
          <div style={styles.hiddenInternalNote}>
            Internal note is hidden.
            <br />
            <span style={{ fontSize: '11px' }}>
              Enable the checkbox to view this note.
            </span>
          </div>
        ) : (
          <>
            <div style={shouldBlur ? styles.blurContent : {}}>
              <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
            </div>

            {/* attachments */}
            {Array.isArray(note.attachments) && note.attachments.length > 0 && (
              <div style={styles.attachmentsWrap}>
                <div style={styles.attachmentsLabel}>
                  <FiPaperclip size={12} /> Attachments ({note.attachments.length})
                </div>

                <div style={styles.attachmentsGrid}>
                  {note.attachments.map((att) => {
                    const href = getAttachmentUrl(att);
                    const displayName = att.originalName || att.name;

                    /* ---- IMAGE: large preview ---- */
                    if (isImage(att.mimetype)) {
                      return (
                        <div
                          key={att._id || att.name}
                          style={styles.attachmentCardImage}
                        >
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.attachmentThumbLinkLarge}
                            title="Open full size"
                            onClick={(e) => {
                              // ✅ NEW: open in the in-app viewer
                              // (Ctrl / Cmd / middle click still opens a new tab)
                              if (!onOpenImage) return;
                              if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                              e.preventDefault();
                              onOpenImage(note, att._id || att.name);
                            }}
                          >
                            <img
                              src={href}
                              alt={displayName}
                              className="cn-att-thumb"
                              style={styles.attachmentThumbLarge}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </a>

                          <div style={styles.attachmentImageFooter}>
                            <div style={styles.attachmentInfo}>
                              <div
                                style={styles.attachmentName}
                                title={displayName}
                              >
                                {displayName}
                              </div>
                              <div style={styles.attachmentMeta}>
                                {formatFileSize(att.size)}
                              </div>
                            </div>

                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={displayName}
                              style={styles.attachmentDownload}
                              title="Download"
                            >
                              <FiDownload size={14} />
                            </a>
                          </div>
                        </div>
                      );
                    }

                    /* ---- OTHER FILES ---- */
                    return (
                      <div key={att._id || att.name} style={styles.attachmentCard}>
                        <div style={styles.attachmentIconBox}>
                          <FiFileText size={20} color="#0066cc" />
                        </div>

                        <div style={styles.attachmentInfo}>
                          <div
                            style={styles.attachmentName}
                            title={displayName}
                          >
                            {displayName}
                          </div>
                          <div style={styles.attachmentMeta}>
                            {formatFileSize(att.size)}
                          </div>
                        </div>

                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={displayName}
                          style={styles.attachmentDownload}
                          title="Download"
                        >
                          <FiDownload size={14} />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {shouldBlur && <div style={styles.hiddenOverlay} />}
          </>
        )}
      </div>

      {/* ✅ NEW: emoji reactions (only the mentioned user can react; everyone sees them) */}
      {!(isInternal && !showThisInternalNote) && (
        <NoteReactions
          caseId={caseId}
          noteId={note._id}
          reactions={note.reactions}
          canReact={isMentionedForMe}
          myUserId={myUserIdNum}
          onUpdated={onReactionsUpdated}
        />
      )}

      <div style={styles.noteActions} className="cn-note-actions">
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* ✅ NEW: Edit (author only, disappears after 7 minutes) */}
          {canEdit && (
            <button
              type="button"
              style={styles.actionBtnEdit}
              onClick={() => onEdit(note)}
              title="You can edit your note for 7 minutes after posting"
            >
              <FiEdit3 size={13} />
              Edit
              <span style={styles.editCountdown}>
                {formatTimeLeft(editTimeLeft)}
              </span>
            </button>
          )}

          {isAdmin && (
            <button
              style={styles.actionBtnAdmin}
              onClick={() => onToggleVisibility(note._id)}
              title={
                isHidden
                  ? 'Make visible to all users'
                  : 'Hide from all users'
              }
            >
              {isHidden ? <FiEye size={13} /> : <FiEyeOff size={13} />}
              {isHidden ? 'Show to All' : 'Hide from All'}
            </button>
          )}

          {!isAdmin && isHidden && (
            <span style={styles.hiddenStatus}>
              <FiShield size={12} />
              This note is hidden from users
            </span>
          )}

          {isAdmin && !isHidden && (
            <span style={styles.visibleStatus}>
              <FiShield size={12} />
              Visible to all users
            </span>
          )}
        </div>

        <span style={{ fontSize: '11px', color: '#868e96' }}>
          {isInternal ? 'For Internal Use' : 'For External Use'}
        </span>
      </div>
    </div>
  );
});

/* ============================================================
   ADD NOTE MODAL
   ============================================================ */
const AddNoteModal = React.memo(function AddNoteModal({
  isOpen,
  onClose,
  onSave,
  isLoading,
  defaultRecipient,
  editNote = null,   // ✅ NEW: when set, the modal edits this note
  onUpdate,          // ✅ NEW: save handler for edit mode
  initialFiles = null, // ✅ NEW: files to attach when the modal opens (STL screenshot)
  overlayZIndex      // ✅ NEW: show the modal above the 3D viewer
}) {
  const isEditMode = !!editNote;
  const [newNoteText, setNewNoteText] = useState('');
  const [subject, setSubject] = useState('case fabrication');
  const [recipient, setRecipient] = useState(defaultRecipient || '');
  const [isExternalNote, setIsExternalNote] = useState(false);
  const [mentions, setMentions] = useState([]);

  // files: [{ file, id }]
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  // editor wrapper element (callback-ref via state so listeners attach reliably)
  const [editorWrapEl, setEditorWrapEl] = useState(null);

  // @mention popup in editor
  const quillRef = useRef(null);
  const outerWrapRef = useRef(null);
  const popoverRef = useRef(null);
  const mentionIndexRef = useRef(null);
  const mentionQueryRef = useRef('');
  const [mentionPopup, setMentionPopup] = useState(null); // { top, left } | null
  const [mentionQuery, setMentionQuery] = useState(''); // text typed after "@"

  useEffect(() => {
    if (defaultRecipient) {
      setRecipient(defaultRecipient);
    }
  }, [defaultRecipient]);

  // ✅ NEW: edit mode - load the note's text + mentions when the modal opens
  useEffect(() => {
    if (!isOpen || !editNote) return;

    setNewNoteText(getNoteBody(editNote));
    setMentions(
      (Array.isArray(editNote.mentions) ? editNote.mentions : [])
        .filter(Boolean)
        .map((m) => ({ userId: m.userId, userName: m.userName }))
    );
    setFiles([]);
    setFileError('');
  }, [isOpen, editNote]);

  // ✅ NEW: screenshot from the STL viewer -> already attached in the notepad
  useEffect(() => {
    if (!isOpen || isEditMode) return;
    if (!Array.isArray(initialFiles) || initialFiles.length === 0) return;

    setFiles(
      initialFiles
        .filter(Boolean)
        .map((f, i) => ({ file: f, id: `init-${Date.now()}-${i}` }))
    );
    setFileError('');
  }, [isOpen, isEditMode, initialFiles]);

  // ✅ NEW: live "time left to edit" inside the modal
  const editTimeLeft = useEditTimeLeft(editNote?.createdAt, isOpen && isEditMode);

  useEffect(() => {
    if (isOpen) {
      setIsExternalNote(false); // ✅ reset to default (unchecked = Internal)
    } else {
      setMentionPopup(null);
      setMentionQuery('');
      mentionQueryRef.current = '';
      mentionIndexRef.current = null;
    }
  }, [isOpen]);

  const isEmpty = useMemo(() => {
    if (isEditMode) {
      // an edited note may keep only its existing attachments
      const hasAttachments =
        Array.isArray(editNote?.attachments) && editNote.attachments.length > 0;
      return !stripHtml(newNoteText) && !hasAttachments;
    }
    return !stripHtml(newNoteText) && files.length === 0;
  }, [newNoteText, files.length, isEditMode, editNote]);

  // ✅ NEW: attachments can't be added while editing (existing ones are kept)
  const isEditModeRef = useRef(isEditMode);
  isEditModeRef.current = isEditMode;

  const updateMentionQuery = useCallback((q) => {
    mentionQueryRef.current = q;
    setMentionQuery(q);
  }, []);

  const closeMentionPopup = useCallback(() => {
    setMentionPopup(null);
    setMentionQuery('');
    mentionQueryRef.current = '';
    mentionIndexRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    setNewNoteText('');
    setIsExternalNote(false);
    setMentions([]);
    setFiles([]);
    setFileError('');
    closeMentionPopup();
    onClose();
  }, [onClose, closeMentionPopup]);

  /* ---------- FILES ---------- */
  const addFiles = useCallback((incoming) => {
    if (isEditModeRef.current) return;
    const list = Array.from(incoming || []).filter(Boolean);
    if (!list.length) return;

    const tooBig = list.filter((f) => f.size > MAX_FILE_SIZE);
    const valid = list.filter((f) => f.size <= MAX_FILE_SIZE);

    setFileError(
      tooBig.length
        ? `${tooBig.length} file(s) skipped: max 25MB each`
        : ''
    );

    if (!valid.length) return;

    setFiles((prev) => {
      const existingKeys = new Set(
        prev.map((f) => `${f.file.name}::${f.file.size}`)
      );
      const next = [...prev];
      valid.forEach((f) => {
        const key = `${f.name}::${f.size}`;
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          next.push({ file: f, id: `${Date.now()}-${Math.random()}` });
        }
      });
      return next;
    });
  }, []);

  const handlePickFiles = () => fileInputRef.current?.click();

  const handleFilesChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    addFiles(picked);

    // reset input so same file can be picked again
    e.target.value = '';
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  /* ---------- PASTE FILES (Ctrl+V) INTO EDITOR ---------- */
  useEffect(() => {
    if (!editorWrapEl) return undefined;

    const onPaste = (e) => {
      const cd = e.clipboardData;
      if (!cd) return;

      let pasted = Array.from(cd.files || []);

      if (!pasted.length && cd.items) {
        pasted = Array.from(cd.items)
          .filter((item) => item.kind === 'file')
          .map((item) => item.getAsFile())
          .filter(Boolean);
      }

      if (!pasted.length) return; // normal text paste -> let Quill handle it

      // If clipboard also has real rich text (e.g. copied from Word), let the
      // text paste normally and still attach the files.
      const html = cd.getData('text/html') || '';
      const hasRichText = stripHtml(html).length > 0;

      if (!hasRichText) {
        e.preventDefault();
        e.stopPropagation();
      }

      addFiles(pasted.map(normalizePastedFile));
    };

    // capture phase so we run before Quill's own paste handler
    editorWrapEl.addEventListener('paste', onPaste, true);
    return () => editorWrapEl.removeEventListener('paste', onPaste, true);
  }, [editorWrapEl, addFiles]);

  /* ---------- DRAG & DROP FILES INTO EDITOR ---------- */
  const handleEditorDragOver = (e) => {
    if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
      e.preventDefault();
    }
  };

  const handleEditorDrop = (e) => {
    const dropped = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
    if (!dropped.length) return;

    e.preventDefault();
    e.stopPropagation();
    addFiles(dropped.map(normalizePastedFile));
  };

  /* ---------- @ MENTION IN EDITOR ---------- */
  const handleEditorChange = useCallback(
    (value, delta, source) => {
      setNewNoteText(value);

      if (source !== 'user') return;

      const ops = (delta && delta.ops) || [];
      let idx = 0;
      let atIndex = null;

      for (const op of ops) {
        if (op.retain) {
          idx += op.retain;
        } else if (op.insert === '@') {
          atIndex = idx;
          break;
        }
      }

      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;

      /* ---- a new "@" was just typed: open the popup ---- */
      if (atIndex !== null) {
        // only trigger when @ starts a word (not inside an email like a@b.com)
        if (atIndex > 0) {
          const prevChar = quill.getText(atIndex - 1, 1);
          if (prevChar && !/\s/.test(prevChar)) return;
        }

        const bounds = quill.getBounds(atIndex);
        const containerRect = quill.container.getBoundingClientRect();
        const outerRect = outerWrapRef.current?.getBoundingClientRect();
        if (!bounds || !outerRect) return;

        const POPUP_WIDTH = 280;
        let left = containerRect.left - outerRect.left + bounds.left;
        const maxLeft = Math.max(0, outerRect.width - POPUP_WIDTH);
        left = Math.min(Math.max(0, left), maxLeft);

        const top = containerRect.top - outerRect.top + bounds.bottom + 6;

        mentionIndexRef.current = atIndex;
        updateMentionQuery('');
        setMentionPopup({ top, left });
        return;
      }

      /* ---- popup already open: whatever is typed after "@" filters users ---- */
      if (mentionIndexRef.current === null) return;

      const sel = quill.getSelection();
      if (!sel) return;

      const start = mentionIndexRef.current;
      const typed = quill.getText(start, Math.max(0, sel.index - start));

      // "@" deleted, or a space/new line typed -> stop mentioning
      if (!typed.startsWith('@') || /\s/.test(typed)) {
        closeMentionPopup();
        return;
      }

      updateMentionQuery(typed.slice(1));
    },
    [updateMentionQuery, closeMentionPopup]
  );

  // When the popup opens: open the picker's list automatically (no need to
  // click the inner "Mention" button) and keep the cursor inside the editor.
  useEffect(() => {
    if (!mentionPopup) return undefined;

    const t1 = setTimeout(() => {
      const root = popoverRef.current;
      if (!root) return;

      if (!root.querySelector(PICKER_SEARCH_SELECTOR)) {
        const toggle = root.querySelector('button');
        if (toggle) {
          toggle.click();
          toggle.style.display = 'none';
        }
      }
    }, 30);

    // the picker may try to grab focus - give it back to the editor
    const t2 = setTimeout(() => {
      quillRef.current?.getEditor?.()?.focus();
    }, 150);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [mentionPopup]);

  // Push the text typed after "@" into the picker's own (hidden) search box,
  // so the user list filters while the user keeps typing in the editor.
  useEffect(() => {
    if (!mentionPopup) return undefined;

    const apply = () => {
      const root = popoverRef.current;
      if (!root) return false;

      const input = root.querySelector(PICKER_SEARCH_SELECTOR);
      if (!input) return false;

      if (input.value === mentionQuery) return true;

      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      ).set;

      setter.call(input, mentionQuery);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    };

    if (apply()) return undefined;

    // list may not be rendered yet (picker is opening) - retry shortly
    const t1 = setTimeout(apply, 80);
    const t2 = setTimeout(apply, 200);
    const t3 = setTimeout(apply, 400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [mentionPopup, mentionQuery]);

  // keyboard while popup is open: Enter/Tab = pick first user, Esc = close
  useEffect(() => {
    if (!editorWrapEl || !mentionPopup) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeMentionPopup();
        return;
      }

      if (e.key === 'Enter' || e.key === 'Tab') {
        const first = popoverRef.current?.querySelector(
          '.note-mention-pop label, .note-mention-pop [role="option"], .note-mention-pop li'
        );

        if (first) {
          e.preventDefault();
          e.stopPropagation();
          first.click();
        }
      }
    };

    // capture phase so Quill does not insert a new line first
    editorWrapEl.addEventListener('keydown', onKeyDown, true);
    return () => editorWrapEl.removeEventListener('keydown', onKeyDown, true);
  }, [editorWrapEl, mentionPopup, closeMentionPopup]);

  // close popup when clicking outside of it
  useEffect(() => {
    if (!mentionPopup) return undefined;

    const onDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        closeMentionPopup();
      }
    };

    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [mentionPopup, closeMentionPopup]);

  // The popup picker always starts empty, so every pick arrives as the last
  // item of the list (this also lets you mention the same person again).
  const handlePickMentionFromEditor = (list) => {
    const nextList = Array.isArray(list) ? list : [];
    const picked = nextList[nextList.length - 1];

    if (!picked) return;

    const pickedKey = picked.userId || picked.userName;

    setMentions((prev) =>
      prev.some((m) => (m.userId || m.userName) === pickedKey)
        ? prev
        : [...prev, picked]
    );

    const quill = quillRef.current?.getEditor?.();
    const idx = mentionIndexRef.current;

    if (quill && idx !== null) {
      const label = `@${picked.userName}`;

      // remove "@" + whatever was typed after it (e.g. "@fat")
      const typedLength = 1 + (mentionQueryRef.current || '').length;

      if (quill.getText(idx, 1) === '@') {
        quill.deleteText(idx, typedLength, 'user');
      }

      quill.insertText(idx, label, { bold: true, color: '#0056b3' }, 'user');
      quill.insertText(
        idx + label.length,
        ' ',
        { bold: false, color: false },
        'user'
      );
      quill.setSelection(idx + label.length + 1, 0, 'silent');
      quill.focus();
    }

    closeMentionPopup();
  };

  const handlePopoverKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeMentionPopup();
      quillRef.current?.getEditor?.()?.focus();
    }
  };

  // if the picker's own search box ever receives focus, return it to the editor
  const handlePopoverFocus = (e) => {
    const t = e.target;
    if (t && t.tagName === 'INPUT' && t.type !== 'checkbox') {
      quillRef.current?.getEditor?.()?.focus();
    }
  };

  const handleSave = async () => {
    const plainText = stripHtml(newNoteText);
    const activeMentions = mentions.filter((m) =>
      plainText.includes(`@${m.userName}`)
    );

    // ✅ NEW: edit mode -> update the existing note instead of creating one
    if (isEditMode) {
      const okEdit = await onUpdate?.({
        noteId: editNote._id,
        html: newNoteText,
        mentions: activeMentions
      });

      if (okEdit) {
        setNewNoteText('');
        setMentions([]);
        closeMentionPopup();
      }
      return;
    }

    // ✅ ALWAYS calculate fresh inside handleSave
    const computedIsInternal = !isExternalNote;

    const ok = await onSave({
      html: newNoteText,
      subject,
      recipient,
      isInternal: computedIsInternal,
      mentions: activeMentions,
      files: files.map((f) => f.file)
    });

    if (ok) {
      setNewNoteText('');
      setIsExternalNote(false);
      setMentions([]);
      setFiles([]);
      setFileError('');
      closeMentionPopup();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        content: styles.modalContent,
        overlay: overlayZIndex
          ? { ...styles.modalOverlay, zIndex: overlayZIndex }
          : styles.modalOverlay
      }}
      contentLabel="Add Note Modal"
    >
      <style>
        {`
          /* single bordered notepad: toolbar + text + previews + footer */
          .note-editor-box .ql-toolbar.ql-snow {
            border: none !important;
            border-bottom: 1px solid #e9ecef !important;
          }

          .note-editor-box .ql-container.ql-snow {
            border: none !important;
            height: auto !important;
            font-size: 14px;
          }

          .note-editor-box .ql-editor {
            min-height: 150px;
            max-height: 240px;
            overflow-y: auto;
          }

          /* @ suggestions list (Teams-like rows) */
          .note-mention-pop > div,
          .note-mention-pop > div > div {
            border: none !important;
            box-shadow: none !important;
            padding: 0;
            margin: 0;
          }

          /* the picker's own search box is hidden: typing happens in the editor */
          .note-mention-pop input[type="text"],
          .note-mention-pop input[type="search"],
          .note-mention-pop input:not([type]) {
            display: none !important;
          }

          .note-mention-pop label {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 8px 10px;
            margin: 0;
            font-size: 14px;
            color: #212529;
            cursor: pointer;
            border-radius: 4px;
          }

          .note-mention-pop label:hover {
            background: #eef4fb;
          }

          .note-mention-pop label:first-of-type {
            background: #f1f6fc;
          }

          @media (max-width: 575px) {
            .add-note-form-row {
              flex-direction: column;
              gap: 12px !important;
            }
          }

          /* ===== ✅ NEW: modal header / footer polish + responsive sizes ===== */
          .an-close-btn:hover { background: rgba(255, 255, 255, 0.28) !important; }
          .an-cancel-btn:hover { background: rgba(255, 255, 255, 0.14) !important; }
          .an-save-btn:hover:not(:disabled) { background: #eef4fb !important; }

          @media (max-width: 767px) {
            .an-header { padding: 14px 16px !important; }
            .an-title { font-size: 15px !important; }
            .an-body { padding: 16px !important; }
            .an-footer { padding: 12px 16px !important; }
            .note-editor-box .ql-editor { min-height: 130px; max-height: 200px; }
          }

          @media (max-width: 575px) {
            .an-header { padding: 12px 14px !important; }
            .an-title { font-size: 14px !important; }
            .an-subtitle { display: none !important; }
            .an-body { padding: 12px !important; }
            .an-footer { padding: 10px 12px !important; gap: 8px !important; }
            .an-footer button { flex: 1; padding: 9px 10px !important; }
            .note-editor-box .ql-toolbar.ql-snow { padding: 4px !important; }
            .note-editor-box .ql-toolbar.ql-snow .ql-formats { margin-right: 6px !important; }
            .note-editor-box .ql-editor { min-height: 120px; max-height: 180px; font-size: 13px; }
          }
        `}
      </style>

      <div style={styles.modalHeader} className="an-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <span style={styles.modalHeaderIcon}>
            <FiMessageSquare size={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            <h3 style={styles.modalTitle} className="an-title">
              {isEditMode ? 'Edit Note' : 'Add New Note'}
            </h3>
            <div style={styles.modalSubtitle} className="an-subtitle">
              {isEditMode
                ? 'Type @ to mention · existing attachments are kept'
                : 'Type @ to mention · Ctrl+V to paste images'}
            </div>
          </div>
        </div>

        <button
          onClick={handleClose}
          style={styles.modalCloseBtn}
          className="an-close-btn"
          aria-label="Close"
          title="Close"
        >
          <FiX size={16} />
        </button>
      </div>

      <div style={styles.modalBody} className="an-body">
        {/* ✅ NEW: edit mode info bar with live countdown */}
        {isEditMode && (
          <div
            style={{
              ...styles.editInfoBar,
              ...(editTimeLeft <= 0 ? styles.editInfoBarExpired : {})
            }}
          >
            <FiClock size={14} />
            {editTimeLeft > 0 ? (
              <span>
                You can edit this note for{' '}
                <strong>{formatTimeLeft(editTimeLeft)}</strong> more.
                Mentioned users will be notified about the update.
              </span>
            ) : (
              <span>
                The 7-minute edit window has ended. This note can no longer be edited.
              </span>
            )}
          </div>
        )}

        {!isEditMode && (
        <>
        <div style={styles.modalFormRow} className="add-note-form-row">
          <div style={styles.modalFormGroup}>
            <label style={styles.modalLabel}>Author</label>
            <input
              type="text"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              style={styles.modalInput}
              placeholder="Enter your name"
            />
          </div>

          <div style={styles.modalFormGroup}>
            <label style={styles.modalLabel}>Subject</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={styles.modalSelect}
            >
              <option value="case fabrication">Case Fabrication</option>
              <option value="case shipping">Case Shipping</option>
              <option value="case invoice">Case Invoice</option>
              <option value="general">General</option>
            </select>
          </div>
        </div>

        <div style={styles.noteTypeToggle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={styles.noteTypeLabel}>
              <input
                type="checkbox"
                checked={isExternalNote} // ✅ checkbox = external
                onChange={(e) => setIsExternalNote(e.target.checked)}
                style={{ marginRight: '8px' }}
              />

              {isExternalNote ? (
                <><FiGlobe style={{ marginRight: '4px' }} /> External Note</>
              ) : (
                <><FiLock style={{ marginRight: '4px' }} /> Internal Note</>
              )}
            </label>

            <span style={{ fontSize: '12px', color: '#6c757d' }}>
              {isExternalNote
                ? '(Visible to external parties)'
                : '(Visible only to team members)'}
            </span>
          </div>
        </div>
        </>
        )}

        {/* outer wrapper is position:relative so the @ popup can float over the editor
            without being clipped by editorWrapper's overflow:hidden */}
        <div ref={outerWrapRef} style={styles.editorOuter}>
          <div
            ref={setEditorWrapEl}
            className="note-editor-box"
            style={styles.editorWrapper}
            onDragOver={handleEditorDragOver}
            onDropCapture={handleEditorDrop}
          >
            <Suspense
              fallback={
                <div
                  style={{
                    ...styles.quillEditor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Spinner animation="border" variant="primary" size="sm" />
                </div>
              }
            >
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={newNoteText}
                onChange={handleEditorChange}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write your note here... (type @ and a name to mention, Ctrl+V to paste images)"
              />
            </Suspense>

            {/* pasted / dropped / attached files preview INSIDE the notepad */}
            {files.length > 0 && (
              <div style={styles.previewStrip}>
                {files.map((f) => (
                  <FilePreview
                    key={f.id}
                    file={f.file}
                    onRemove={() => removeFile(f.id)}
                  />
                ))}
              </div>
            )}

            {/* small in-editor footer (paperclip is inside the notepad, like Teams) */}
            {/* ✅ hidden in edit mode - attachments can only be added to new notes */}
            {!isEditMode && (
            <div style={styles.editorFooter}>
              <button
                type="button"
                onClick={handlePickFiles}
                style={styles.editorFooterBtn}
                title="Attach files (max 25MB each)"
              >
                <FiPaperclip size={16} />
              </button>

              <span style={styles.editorFooterHint}>
                {fileError ? (
                  <span style={{ color: '#dc3545' }}>{fileError}</span>
                ) : (
                  'Paste images with Ctrl+V or drag files here'
                )}
              </span>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFilesChange}
                style={{ display: 'none' }}
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
              />
            </div>
            )}
          </div>

          {mentionPopup && (
            <div
              ref={popoverRef}
              style={{
                ...styles.mentionPopover,
                top: mentionPopup.top,
                left: mentionPopup.left
              }}
              onKeyDown={handlePopoverKeyDown}
              onFocus={handlePopoverFocus}
              // keep the cursor inside the editor while clicking a user
              onMouseDown={(e) => e.preventDefault()}
            >
              <div style={styles.mentionPopoverTitle}>
                <FiAtSign size={12} /> Suggestions
                {mentionQuery ? ` · "${mentionQuery}"` : ''}
              </div>
              <div className="note-mention-pop" style={styles.mentionPopoverList}>
                <UserMentionPicker
                  selected={[]}
                  onChange={handlePickMentionFromEditor}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={styles.modalFooter} className="an-footer">
        <button
          onClick={handleClose}
          style={styles.modalCancelBtn}
          className="an-cancel-btn"
        >
          Cancel
        </button>

        <button
          onClick={handleSave}
          style={{
            ...styles.modalSaveBtn,
            opacity: isEmpty || (isEditMode && editTimeLeft <= 0) ? 0.5 : 1
          }}
          className="an-save-btn"
          disabled={isEmpty || isLoading || (isEditMode && editTimeLeft <= 0)}
        >
          {isLoading ? (
            <Spinner size="sm" animation="border" style={{ color: '#004aad' }} />
          ) : isEditMode ? (
            'Update Note'
          ) : (
            'Save Note'
          )}
        </button>
      </div>
    </Modal>
  );
});

/* ============================================================
   ✅ NEW: PROMINENT TOAST (replaces the plain react-bootstrap Toast)
   - success / error / warning / info with icon, title, message
   - slides in, progress bar shows time left, pauses on hover
   - rendered in a portal so no parent can clip it
   ============================================================ */
const TOAST_DURATION_MS = 4000;

const TOAST_THEMES = {
  success: {
    accent: '#16a34a',
    soft: '#e8f7ee',
    icon: FiCheckCircle,
    title: 'Success'
  },
  error: {
    accent: '#dc2626',
    soft: '#fdecec',
    icon: FiAlertCircle,
    title: 'Something went wrong'
  },
  warning: {
    accent: '#d97706',
    soft: '#fff4e0',
    icon: FiAlertTriangle,
    title: 'Please note'
  },
  info: {
    accent: '#004aad',
    soft: '#e8f0fb',
    icon: FiInfo,
    title: 'Notice'
  }
};

// guess the type from the text when the caller doesn't pass one
const inferToastType = (msg) => {
  const m = String(msg || '').toLowerCase();
  if (/(fail|error|invalid|missing|cannot|can't|not found|denied|unauthori)/.test(m)) {
    return 'error';
  }
  if (/(only|expired|can no longer|within)/.test(m)) return 'warning';
  if (/(success|added|updated|saved|deleted|removed)/.test(m)) return 'success';
  return 'info';
};

const TOAST_CSS = `
  @keyframes cnToastIn {
    from { opacity: 0; transform: translate3d(110%, 0, 0) scale(.96); }
    to   { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
  }
  @keyframes cnToastOut {
    from { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    to   { opacity: 0; transform: translate3d(110%, 0, 0) scale(.96); }
  }
  @keyframes cnToastBar {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
  }
  @keyframes cnToastPop {
    0%   { transform: scale(.4); opacity: 0; }
    60%  { transform: scale(1.15); opacity: 1; }
    100% { transform: scale(1); }
  }

  .cn-toast-wrap {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 13000;
    pointer-events: none;
  }

  .cn-toast {
    pointer-events: auto;
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 380px;
    max-width: calc(100vw - 40px);
    background: #fff;
    border-radius: 12px;
    padding: 14px 40px 16px 14px;
    box-shadow: 0 18px 45px rgba(15, 23, 42, 0.22), 0 4px 12px rgba(15, 23, 42, 0.08);
    border: 1px solid rgba(15, 23, 42, 0.06);
    border-left: 5px solid var(--cn-accent);
    overflow: hidden;
    animation: cnToastIn .38s cubic-bezier(.21, 1.02, .73, 1) both;
  }

  .cn-toast.is-leaving { animation: cnToastOut .28s ease-in both; }

  .cn-toast-icon {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--cn-soft);
    color: var(--cn-accent);
    animation: cnToastPop .45s .12s ease-out both;
  }

  .cn-toast-text { min-width: 0; flex: 1; }

  .cn-toast-title {
    font-size: 14.5px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
    margin: 1px 0 3px;
  }

  .cn-toast-msg {
    font-size: 13px;
    color: #475569;
    line-height: 1.45;
    word-break: break-word;
  }

  .cn-toast-close {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: background .15s, color .15s;
  }

  .cn-toast-close:hover { background: #f1f5f9; color: #0f172a; }

  .cn-toast-bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: var(--cn-accent);
    opacity: .85;
    transform-origin: left center;
    animation: cnToastBar var(--cn-duration) linear forwards;
  }

  .cn-toast:hover .cn-toast-bar { animation-play-state: paused; }

  @media (max-width: 575px) {
    .cn-toast-wrap { top: 12px; left: 12px; right: 12px; }
    .cn-toast { width: 100%; max-width: 100%; padding: 12px 38px 14px 12px; }
    .cn-toast-icon { width: 34px; height: 34px; }
    .cn-toast-title { font-size: 14px; }
    .cn-toast-msg { font-size: 12.5px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .cn-toast, .cn-toast.is-leaving, .cn-toast-icon { animation: none; }
  }
`;

const NoteToast = React.memo(function NoteToast({ toast, onClose }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    setLeaving(false);
  }, [toast?.id]);

  if (!toast || typeof document === 'undefined') return null;

  const theme = TOAST_THEMES[toast.type] || TOAST_THEMES.info;
  const Icon = theme.icon;

  const close = () => setLeaving(true);

  return createPortal(
    <div className="cn-toast-wrap">
      <style>{TOAST_CSS}</style>
      <div
        key={toast.id}
        className={`cn-toast ${leaving ? 'is-leaving' : ''}`}
        role={toast.type === 'error' ? 'alert' : 'status'}
        aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
        style={{
          '--cn-accent': theme.accent,
          '--cn-soft': theme.soft,
          '--cn-duration': `${TOAST_DURATION_MS}ms`
        }}
        onAnimationEnd={(e) => {
          if (e.animationName === 'cnToastOut') onClose(toast.id);
        }}
      >
        <span className="cn-toast-icon">
          <Icon size={20} />
        </span>

        <div className="cn-toast-text">
          <div className="cn-toast-title">{toast.title || theme.title}</div>
          {toast.message && <div className="cn-toast-msg">{toast.message}</div>}
        </div>

        <button
          type="button"
          className="cn-toast-close"
          onClick={close}
          aria-label="Close notification"
          title="Close"
        >
          <FiX size={15} />
        </button>

        {/* progress bar: when it finishes the toast slides out (pauses on hover) */}
        <span
          className="cn-toast-bar"
          onAnimationEnd={(e) => {
            e.stopPropagation();
            if (e.animationName === 'cnToastBar') close();
          }}
        />
      </div>
    </div>,
    document.body
  );
});

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
function CaseTab({ components, caseId, onProcedureUpdate }) {
  const [key, setKey] = useState('Details');
  const [notes, setNotes] = useState([]);
  const [editorHtml, setEditorHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // ✅ CHANGED: one toast object { id, type, title, message } instead of plain text
  const [toast, setToast] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ✅ NEW: target note id (from ?noteId=) to scroll to & highlight
  const [targetNoteId, setTargetNoteId] = useState(null);
  const highlightedRef = useRef(null);

  // ✅ NEW: notes board filters
  const [filterUserKey, setFilterUserKey] = useState(null);   // selected user (normalized name)
  const [filterMode, setFilterMode] = useState('all');        // 'all' | 'authored' | 'mentioned'
  const [viewMode, setViewMode] = useState('both');           // 'both' | 'external' | 'internal'

  // showMessage(message, type?, title?)  - type: 'success' | 'error' | 'warning' | 'info'
  const showMessage = useCallback((msg, type, title) => {
    setToast({
      id: `${Date.now()}-${Math.random()}`,
      type: type || inferToastType(msg),
      title: title || '',
      message: msg || ''
    });
  }, []);

  const closeToast = useCallback((id) => {
    setToast((t) => (t && t.id === id ? null : t));
  }, []);

  /* CURRENT USER */
  const currentUser = useMemo(() => {
    let userInfo = {};

    try {
      userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    } catch (error) {
      console.error('Error parsing userInfo:', error);
    }

    return {
      userName:
        localStorage.getItem('userName') ||
        userInfo.userName ||
        userInfo.name ||
        'Unknown User',

      userRole:
        localStorage.getItem('userRole') ||
        userInfo.userRole ||
        userInfo.role ||
        'User',

      userId:
        localStorage.getItem('userId') ||
        userInfo.userId ||
        userInfo._id ||
        ''
    };
  }, []);

  const isAdmin = useMemo(
    () => (currentUser.userRole || '').toLowerCase() === 'admin',
    [currentUser.userRole]
  );

  const defaultRecipient = useMemo(
    () => localStorage.getItem('userName') || currentUser.userName || '',
    [currentUser.userName]
  );

  // ✅ NEW: numeric current user id for mention matching
  const myUserIdNum = useMemo(() => {
    const n = Number(currentUser.userId);
    return Number.isNaN(n) ? null : n;
  }, [currentUser.userId]);

  const normalizeNote = useCallback((note) => {
    if (!note) return null;

    return {
      ...note,
      isInternal: toBool(note.isInternal),
      showInternalContent: note.showInternalContent !== false,
      attachments: Array.isArray(note.attachments) ? note.attachments : [],
      // ✅ NEW
      reactions: Array.isArray(note.reactions) ? note.reactions : [],
    };
  }, []);

  const fetchControllerRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    if (!caseId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    setIsLoading(true);

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(
        `${API_BASE_URL}/api/cases/${caseId}/notes`,
        {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch notes: ${response.status}`);
      }

      const data = await response.json();

      const fetchedNotes = Array.isArray(data.notes) ? data.notes : [];

      const normalizedNotes = sortByCreatedAt(
        fetchedNotes.map(normalizeNote).filter(Boolean)
      );

      setNotes(normalizedNotes);
      setEditorHtml(data.editorHtml || '');
      setIsLoading(false);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error('Error fetching notes:', error);
      showMessage('Failed to fetch notes. Please refresh and try again.', 'error', 'Could not load notes');
      setIsLoading(false);
    }
  }, [caseId, normalizeNote, showMessage]);

  useEffect(() => {
    fetchNotes();

    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
      }
    };
  }, [fetchNotes]);

  // ✅ NEW: read ?noteId= on mount, switch to Notes tab immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const noteId = params.get('noteId');
    if (noteId) {
      setTargetNoteId(noteId);
      setKey('Notes');
    }
  }, []);

  // ✅ NEW: once Notes tab active AND notes loaded → scroll & highlight target note
  useEffect(() => {
    if (key !== 'Notes' || !targetNoteId || notes.length === 0) return;

    const timer = setTimeout(() => {
      const el = document.getElementById(targetNoteId);
      if (!el) return;

      // clear previous outline
      if (highlightedRef.current && highlightedRef.current !== el) {
        highlightedRef.current.style.outline = '';
      }

      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.outline = '3px solid #ffc107';
      highlightedRef.current = el;

      setTimeout(() => {
        if (highlightedRef.current === el) {
          el.style.outline = '';
          highlightedRef.current = null;
        }
      }, 5000);
    }, 400);

    return () => clearTimeout(timer);
  }, [key, targetNoteId, notes]);

  const toggleNoteVisibility = useCallback(
    async (noteId) => {
      if (!isAdmin) {
        showMessage('Only administrators can hide/show notes', 'warning', 'Not allowed');
        return;
      }

      if (!noteId || !caseId) {
        showMessage('Invalid note');
        return;
      }

      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${API_BASE_URL}/api/cases/${caseId}/notes/${noteId}/toggle-visibility`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              userId: currentUser.userId,
              userName: currentUser.userName
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to toggle visibility: ${response.status}`);
        }

        const data = await response.json();

        if (data.note) {
          const updatedNote = normalizeNote(data.note);

          setNotes((prevNotes) =>
            prevNotes.map((note) =>
              note._id === noteId ? { ...note, ...updatedNote } : note
            )
          );
        } else {
          await fetchNotes();
        }

        showMessage(data.message || 'Note visibility updated', 'success', 'Visibility updated');
      } catch (error) {
        console.error('Error toggling note visibility:', error);
        showMessage('Failed to update note visibility', 'error', 'Update failed');
      }
    },
    [
      isAdmin,
      caseId,
      currentUser.userId,
      currentUser.userName,
      normalizeNote,
      fetchNotes,
      showMessage
    ]
  );

  const toggleInternalNoteVisibility = useCallback((noteId, e) => {
    if (e) {
      e.stopPropagation();
    }

    setNotes((prevNotes) =>
      prevNotes.map((note) =>
        note._id === noteId
          ? { ...note, showInternalContent: note.showInternalContent === false }
          : note
      )
    );
  }, []);

  // ✅ NEW: in-app image viewer { noteId, attId } - note is read live from `notes`
  const [imageViewer, setImageViewer] = useState(null);

  const openImageViewer = useCallback((note, attId) => {
    if (!note) return;
    setImageViewer({ noteId: note._id, attId });
  }, []);

  const closeImageViewer = useCallback(() => setImageViewer(null), []);

  const imageViewerNote = useMemo(
    () =>
      imageViewer ? notes.find((n) => n._id === imageViewer.noteId) || null : null,
    [imageViewer, notes]
  );

  // ✅ NEW: update a single note's reactions after the API call succeeds
  const handleReactionsUpdated = useCallback((noteId, reactions) => {
    setNotes((prevNotes) =>
      prevNotes.map((n) => (n._id === noteId ? { ...n, reactions } : n))
    );
  }, []);

  // ✅ NEW: note being edited (null = "Add Note" mode)
  const [editingNote, setEditingNote] = useState(null);

  // ✅ NEW: screenshot coming from the STL viewer
  const [prefillFiles, setPrefillFiles] = useState(null);
  const [noteModalOnTop, setNoteModalOnTop] = useState(false);

  const openModal = useCallback(() => {
    setEditingNote(null);
    setPrefillFiles(null);
    setNoteModalOnTop(false);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingNote(null);
  }, []);

  // ✅ NEW: clear the screenshot once the modal is closed (saved or cancelled)
  useEffect(() => {
    if (!isModalOpen) {
      setPrefillFiles(null);
      setNoteModalOnTop(false);
    }
  }, [isModalOpen]);

  // ✅ NEW: listen for "Add Note" from the STL viewer screenshot editor
  useEffect(() => {
    const onProbe = (e) => {
      e.preventDefault(); // tells the viewer that this page can add notes
    };

    const onAddNote = (e) => {
      const file = e?.detail?.file;
      if (!file) return;
      e.preventDefault(); // accepted

      setEditingNote(null);
      setPrefillFiles([file]);
      setNoteModalOnTop(true);
      setIsModalOpen(true);
    };

    window.addEventListener(STL_SHOT_PROBE_EVENT, onProbe);
    window.addEventListener(STL_SHOT_ADD_NOTE_EVENT, onAddNote);
    return () => {
      window.removeEventListener(STL_SHOT_PROBE_EVENT, onProbe);
      window.removeEventListener(STL_SHOT_ADD_NOTE_EVENT, onAddNote);
    };
  }, []);

  // ✅ NEW: while the note modal is over the 3D viewer, Esc closes only the
  // note modal (otherwise the viewer behind it would close too)
  useEffect(() => {
    if (!isModalOpen || !noteModalOnTop) return undefined;

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      e.stopPropagation();
      setIsModalOpen(false);
      setEditingNote(null);
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isModalOpen, noteModalOnTop]);

  // ✅ NEW: open the modal in edit mode (only while the 7 minutes are running)
  const openEditNote = useCallback(
    (note) => {
      if (!note) return;
      if (getEditTimeLeft(note.createdAt) <= 0) {
        showMessage('Notes can only be edited within 7 minutes of posting', 'warning', 'Edit time is over');
        return;
      }
      setEditingNote(note);
      setIsModalOpen(true);
    },
    [showMessage]
  );

  // ✅ NEW: save an edited note (server checks author + 7-minute window again)
  const updateNote = useCallback(
    async ({ noteId, html, mentions }) => {
      if (!caseId || !noteId) {
        showMessage('Invalid note');
        return false;
      }

      setIsLoading(true);

      try {
        const token = localStorage.getItem('accessToken');
        const mentionedUserIds = (mentions || []).map((m) => m.userId);

        const response = await fetch(
          `${API_BASE_URL}/api/cases/${caseId}/notes/${noteId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              bodyHtml: html || '',
              mentionedUserIds
            })
          }
        );

        let data = {};
        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (!response.ok) {
          throw new Error(
            data?.message || `Failed to update note: ${response.status}`
          );
        }

        const updated = data.note || data.newNote;

        if (updated) {
          const normalized = normalizeNote(updated);
          setNotes((prevNotes) =>
            prevNotes.map((n) =>
              n._id === noteId
                ? {
                    ...n,
                    ...normalized,
                    // keep the local "Show" checkbox state of internal notes
                    showInternalContent: n.showInternalContent
                  }
                : n
            )
          );
        } else {
          await fetchNotes();
        }

        showMessage(
          'Your changes were saved. Mentioned users have been notified.',
          'success',
          'Note updated'
        );
        setIsModalOpen(false);
        setEditingNote(null);

        return true;
      } catch (error) {
        console.error('Error updating note:', error);
        showMessage(error.message || 'Failed to update note', 'error', 'Could not update note');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [caseId, normalizeNote, fetchNotes, showMessage]
  );

  const addNote = useCallback(
    async ({ html, subject, recipient, isInternal, mentions, files }) => {
      const filesArr = Array.isArray(files) ? files : [];
      const hasText = !!stripHtml(html);

      if (!hasText && filesArr.length === 0) {
        showMessage('Write something or attach a file before saving.', 'warning', 'Note is empty');
        return false;
      }

      if (!caseId) {
        showMessage('Case ID is missing', 'error', 'Could not add note');
        return false;
      }

      setIsLoading(true);

      try {
        // ✅ CHANGED: header and body are kept separately too, so the body
        // can be edited later without touching the "From / Created" header
        const headerHtml = buildNoteHeader({
          userName: currentUser.userName,
          userRole: currentUser.userRole,
          recipient,
          subject
        });
        const bodyHtml = html || '';

        const formattedNote = `${headerHtml}${bodyHtml}
      `;

        const token = localStorage.getItem('accessToken');
        const mentionedUserIds = (mentions || []).map((m) => m.userId);

        let response;

        if (filesArr.length > 0) {
          // === multipart/form-data path ===
          const formData = new FormData();
          formData.append('text', formattedNote);
          formData.append('createdBy', recipient || currentUser.userName || 'Unknown User');
          formData.append('isInternal', String(!!isInternal));
          formData.append('createdByUserId', currentUser.userId || '');
          formData.append('subject', subject || 'case fabrication');
          formData.append('mentionedUserIds', JSON.stringify(mentionedUserIds));
          formData.append('userRole', currentUser.userRole || 'User');
          formData.append('headerHtml', headerHtml);
          formData.append('bodyHtml', bodyHtml);

          filesArr.forEach((f) => {
            formData.append('attachments', f);
          });

          response = await fetch(
            `${API_BASE_URL}/api/cases/${caseId}/notes`,
            {
              method: 'POST',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              // NOTE: do NOT set Content-Type; browser sets boundary
              body: formData
            }
          );
        } else {
          // === original JSON path (unchanged) ===
          response = await fetch(
            `${API_BASE_URL}/api/cases/${caseId}/notes`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              },
              body: JSON.stringify({
                text: formattedNote,
                createdBy: recipient || currentUser.userName || 'Unknown User',
                isInternal,
                createdByUserId: currentUser.userId || '',
                subject: subject || 'case fabrication',
                mentionedUserIds,
                headerHtml,
                bodyHtml
              })
            }
          );
        }

        if (!response.ok) {
          let backendMessage = `Failed to add note: ${response.status}`;

          try {
            const errorData = await response.json();

            if (errorData?.message) {
              backendMessage = errorData.message;
            }

            if (errorData?.error) {
              backendMessage = errorData.error;
            }
          } catch (jsonError) {
            console.error('Could not parse backend error:', jsonError);
          }

          throw new Error(backendMessage);
        }

        const data = await response.json();

        if (data.newNote) {
          const savedNote = normalizeNote(data.newNote);

          setNotes((prevNotes) => sortByCreatedAt([...prevNotes, savedNote]));
        } else {
          await fetchNotes();
        }

        showMessage(
          mentionedUserIds.length > 0
            ? `Your note was saved and ${mentionedUserIds.length} mentioned user${mentionedUserIds.length > 1 ? 's were' : ' was'} notified.`
            : 'Your note was saved to this case.',
          'success',
          'Note added successfully'
        );
        setIsModalOpen(false);

        return true;
      } catch (error) {
        console.error('Error adding note:', error);

        showMessage(error.message || 'Failed to add note', 'error', 'Could not add note');

        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [
      caseId,
      currentUser.userName,
      currentUser.userRole,
      currentUser.userId,
      normalizeNote,
      fetchNotes,
      showMessage
    ]
  );

  const safeEditorHtml = useMemo(
    () => (editorHtml ? DOMPurify.sanitize(editorHtml) : ''),
    [editorHtml]
  );

  const totalNotesCount = notes.length + (editorHtml ? 1 : 0);

  /* ============================================================
     ✅ NEW: user list for the filter (authors + mentioned users)
     ============================================================ */
  const userOptions = useMemo(() => {
    const byKey = new Map();
    const idToKey = new Map();

    const add = (name, id, kind) => {
      const idStr =
        id !== undefined && id !== null && id !== '' ? String(id) : null;

      // same person may appear with a slightly different name -> merge by id
      let key = idStr && idToKey.has(idStr) ? idToKey.get(idStr) : normName(name);
      if (IGNORED_NAMES.has(key)) return;

      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          name: String(name).trim(),
          ids: new Set(),
          authored: 0,
          mentioned: 0
        });
      }

      const entry = byKey.get(key);
      if (idStr) {
        entry.ids.add(idStr);
        idToKey.set(idStr, key);
      }
      entry[kind] += 1;
    };

    notes.forEach((n) => {
      add(n.createdBy, n.authorUserId, 'authored');

      const seen = new Set();
      (Array.isArray(n.mentions) ? n.mentions : []).forEach((m) => {
        if (!m) return;
        const k = String(m.userId ?? m.userName);
        if (seen.has(k)) return;
        seen.add(k);
        add(m.userName, m.userId, 'mentioned');
      });
    });

    return Array.from(byKey.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [notes]);

  const selectedUser = useMemo(
    () => userOptions.find((o) => o.key === filterUserKey) || null,
    [userOptions, filterUserKey]
  );

  const filteredNotes = useMemo(
    () =>
      selectedUser
        ? notes.filter((n) => noteMatchesUser(n, selectedUser, filterMode))
        : notes,
    [notes, selectedUser, filterMode]
  );

  const externalNotes = useMemo(
    () => filteredNotes.filter((n) => !toBool(n.isInternal)),
    [filteredNotes]
  );

  const internalNotes = useMemo(
    () => filteredNotes.filter((n) => toBool(n.isInternal)),
    [filteredNotes]
  );

  const clearFilter = useCallback(() => {
    setFilterUserKey(null);
    setFilterMode('all');
  }, []);

  const handleSelectUser = useCallback((k) => {
    setFilterUserKey(k);
    if (!k) setFilterMode('all');
  }, []);

  const renderNote = (note) => {
    // ✅ only mark as "mentioned you" if the current user
    //    appears in the note's mentions
    const isMentionedForMe =
      myUserIdNum !== null &&
      Array.isArray(note.mentions) &&
      note.mentions.some((m) => m && Number(m.userId) === myUserIdNum);

    // ✅ NEW: did the current user write this note? (server saves authorUserId)
    const isMine =
      myUserIdNum !== null &&
      note.authorUserId !== undefined &&
      note.authorUserId !== null &&
      Number(note.authorUserId) === myUserIdNum;

    return (
      <NoteItem
        key={note._id}
        note={note}
        isAdmin={isAdmin}
        isMentionedForMe={isMentionedForMe}
        onToggleVisibility={toggleNoteVisibility}
        onToggleInternal={toggleInternalNoteVisibility}
        caseId={caseId}
        myUserIdNum={myUserIdNum}
        onReactionsUpdated={handleReactionsUpdated}
        isMine={isMine}
        onEdit={openEditNote}
        onOpenImage={openImageViewer}
      />
    );
  };

  /* ---- render the External | Internal board ----
     ✅ FIX: each side is now its own independent column (no shared rows),
     so every note only takes the height of its own content - a short note
     no longer stretches to match a long note next to it. */
  const renderBoard = () => {
    const showExt = viewMode !== 'internal';
    const showInt = viewMode !== 'external';
    const isBoth = showExt && showInt;

    const forUser = selectedUser ? ` for ${selectedUser.name}` : '';

    const emptySlot = (type) => (
      <div className="cn-empty-slot">
        {type === 'ext' ? <FiGlobe size={14} /> : <FiLock size={14} />}
        {type === 'ext'
          ? `No external notes${forUser}`
          : `No internal notes${forUser}`}
      </div>
    );

    const renderColumn = (type) => {
      const list = type === 'ext' ? externalNotes : internalNotes;

      return (
        <div className={`cn-col ${type}`}>
          <div className={`cn-col-head ${type}`}>
            <span className="cn-col-title">
              {type === 'ext' ? <FiGlobe size={14} /> : <FiLock size={14} />}
              {type === 'ext' ? 'External Notes' : 'Internal Notes'}
            </span>
            <span className="cn-col-count">{list.length}</span>
          </div>

          {list.length === 0 ? (
            <div className={`cn-cell ${type} is-empty`}>{emptySlot(type)}</div>
          ) : (
            list.map((note) => (
              <div key={`${type}-${note._id}`} className={`cn-cell ${type}`}>
                <span className={`cn-cell-tag ${type}`}>
                  {type === 'ext' ? <FiGlobe size={10} /> : <FiLock size={10} />}
                  {type === 'ext' ? 'External' : 'Internal'}
                </span>
                {renderNote(note)}
              </div>
            ))
          )}
        </div>
      );
    };

    return (
      <div className={`cn-board ${isBoth ? 'both' : 'single'}`}>
        {showExt && renderColumn('ext')}
        {showInt && renderColumn('int')}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @media (max-width: 767px) {

            .case-tabs-wrapper {
              width: 100%;
              overflow-x: auto;
              overflow-y: hidden;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
            }

            .case-tabs-wrapper::-webkit-scrollbar {
              display: none;
            }

            .case-tabs-wrapper .custom-tabs {
              display: flex !important;
              flex-wrap: nowrap !important;
              width: max-content !important;
              min-width: 100% !important;
              margin-bottom: 8px !important;
            }

            .case-tabs-wrapper .custom-tabs .nav-item {
              flex: 0 0 auto !important;
              white-space: nowrap !important;
            }

            .case-tabs-wrapper .custom-tabs .nav-link {
              white-space: nowrap !important;
              font-size: 10px !important;
              padding: 6px 9px !important;
              margin: 0 !important;
              line-height: 1.2 !important;
            }

            .case-tabs-wrapper .custom-tabs .nav-link svg {
              width: 11px;
              height: 11px;
            }

            .case-tabs-wrapper .notes-mobile {
              padding: 10px !important;
            }

            .case-modal-mobile {
              width: 94% !important;
              max-width: 94% !important;
            }

            .case-modal-mobile .ql-editor {
              min-height: 180px !important;
            }

          }

          @media (max-width: 400px) {

            .case-tabs-wrapper .custom-tabs .nav-link {
              font-size: 9px !important;
              padding: 5px 7px !important;
            }

          }

          /* =====================================================
             ✅ NEW: Notes board (External | Internal) + filters
             ===================================================== */

          .cn-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 12px 16px;
            background: #fff;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            padding: 12px 14px;
            margin-bottom: 16px;
            box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
          }

          .cn-toolbar-group {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            min-width: 0;
          }

          .cn-toolbar-label {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            color: #6c757d;
            white-space: nowrap;
          }

          .cn-toolbar-spacer { flex: 1; }

          /* ---- user filter (searchable dropdown) ---- */
          .cn-user-filter {
            position: relative;
            width: 280px;
            max-width: 100%;
          }

          .cn-user-input {
            display: flex;
            align-items: center;
            gap: 8px;
            height: 36px;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            padding: 0 10px;
            background: #fff;
            cursor: text;
            transition: border-color .15s, box-shadow .15s;
          }

          .cn-user-input:hover { border-color: #ced4da; }

          .cn-user-input.is-open,
          .cn-user-input:focus-within {
            border-color: #0066cc;
            box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.12);
          }

          .cn-user-input input {
            border: none;
            outline: none;
            flex: 1;
            min-width: 0;
            font-size: 14px;
            background: transparent;
            color: #212529;
            padding: 0;
          }

          .cn-muted { color: #868e96; flex: 0 0 auto; }

          .cn-clear {
            border: none;
            background: #e9ecef;
            color: #495057;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            cursor: pointer;
            flex: 0 0 auto;
          }

          .cn-clear:hover { background: #dee2e6; }

          .cn-user-menu {
            position: absolute;
            top: calc(100% + 6px);
            left: 0;
            right: 0;
            min-width: 240px;
            z-index: 40;
            background: #fff;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            box-shadow: 0 12px 32px rgba(16, 24, 40, 0.14);
            padding: 6px;
            max-height: 320px;
            overflow-y: auto;
          }

          .cn-user-opt {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            border: none;
            background: transparent;
            padding: 7px 8px;
            border-radius: 6px;
            cursor: pointer;
            text-align: left;
          }

          .cn-user-opt:hover,
          .cn-user-opt.is-active { background: #eef4fb; }

          .cn-user-opt.is-selected .cn-user-opt-name { color: #0066cc; font-weight: 600; }

          .cn-user-opt-text {
            display: flex;
            flex-direction: column;
            min-width: 0;
            flex: 1;
            line-height: 1.25;
          }

          .cn-user-opt-name {
            font-size: 14px;
            font-weight: 500;
            color: #212529;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .cn-user-opt-meta { font-size: 11px; color: #868e96; }

          .cn-user-empty {
            padding: 12px;
            font-size: 13px;
            color: #868e96;
            text-align: center;
          }

          .cn-avatar {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            color: #fff;
            font-size: 12px;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
          }

          .cn-avatar.sm { width: 22px; height: 22px; font-size: 10px; }
          .cn-avatar.all { background: #495057; }

          /* ---- segmented controls ---- */
          .cn-seg {
            display: inline-flex;
            background: #f1f3f5;
            border-radius: 7px;
            padding: 3px;
            gap: 2px;
          }

          .cn-seg button {
            border: none;
            background: transparent;
            padding: 5px 12px;
            border-radius: 5px;
            font-size: 12px;
            font-weight: 500;
            color: #495057;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            white-space: nowrap;
            transition: background .15s, color .15s;
          }

          .cn-seg button:hover:not(:disabled):not(.is-on) { color: #212529; }

          .cn-seg button.is-on {
            background: #fff;
            color: #0066cc;
            box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
          }

          .cn-seg button:disabled { opacity: .45; cursor: not-allowed; }

          .cn-seg-count {
            background: #e9ecef;
            color: #495057;
            border-radius: 9px;
            padding: 0 6px;
            font-size: 11px;
            font-weight: 600;
          }

          .cn-seg button.is-on .cn-seg-count { background: #e7f1fb; color: #0066cc; }

          /* ---- active filter summary ---- */
          .cn-result {
            flex-basis: 100%;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            font-size: 12px;
            color: #495057;
            background: #f4f8fd;
            border: 1px solid #dbe8f7;
            border-radius: 6px;
            padding: 6px 10px;
          }

          .cn-result strong { color: #0056b3; }

          .cn-link-btn {
            border: none;
            background: none;
            color: #0066cc;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
            padding: 0;
            margin-left: auto;
            display: inline-flex;
            align-items: center;
            gap: 3px;
          }

          .cn-link-btn:hover { text-decoration: underline; }

          /* ---- board: two independent columns ----
             ✅ FIX: align-items: start + each column stacks its own notes,
             so a note's height = its own content (not the tallest in the row) */
          .cn-board {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px 16px;
            align-items: start;
          }

          .cn-board.single { grid-template-columns: minmax(0, 1fr); }

          .cn-col {
            display: flex;
            flex-direction: column;
            gap: 12px;
            min-width: 0;
          }

          .cn-col-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 10px 14px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .cn-col-title { display: flex; align-items: center; gap: 6px; }

          .cn-col-head.ext {
            background: #e3f4f7;
            color: #0c5460;
            border-left: 4px solid #17a2b8;
          }

          .cn-col-head.int {
            background: #fff6d9;
            color: #856404;
            border-left: 4px solid #ffc107;
          }

          .cn-col-count {
            background: rgba(255, 255, 255, 0.85);
            border-radius: 10px;
            padding: 1px 9px;
            font-size: 12px;
          }

          .cn-cell {
            min-width: 0;
            display: flex;
            flex-direction: column;
          }

          .cn-cell > .cn-note {
            flex: 0 0 auto;
            margin-bottom: 0 !important;
            border-top: 1px solid #edf0f2;
            border-right: 1px solid #edf0f2;
            border-bottom: 1px solid #edf0f2;
            box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          .cn-cell > .cn-note:hover {
            box-shadow: 0 4px 14px rgba(16, 24, 40, 0.08);
          }

          .cn-note-content img { max-width: 100%; height: auto; }
          .cn-note-content table { max-width: 100%; display: block; overflow-x: auto; }

          .cn-cell-tag { display: none; }

          .cn-empty-slot {
            min-height: 64px;
            border: 1px dashed #dee2e6;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            color: #adb5bd;
            font-size: 12px;
            background: rgba(255, 255, 255, 0.55);
            padding: 12px;
            text-align: center;
          }

          .cn-filter-empty {
            background: #fff;
            border: 1px dashed #dee2e6;
            border-radius: 8px;
            padding: 32px 16px;
            text-align: center;
            color: #6c757d;
            font-size: 14px;
          }

          /* ---- tablet ---- */
          @media (max-width: 991px) {
            .cn-user-filter { width: 230px; }
            .cn-cell > .cn-note { padding: 14px 14px !important; }
          }

          /* =====================================================
             ✅ CHANGED: responsive notes board
             - tablets / foldables / small windows: BOTH columns stay
               side by side (External | Internal), everything gets
               smaller step by step as the screen gets narrower
             - only real phone widths (<= 480px): one note per row
             ===================================================== */

          /* ---- small tablets / foldables ---- */
          @media (max-width: 767px) {
            .cn-toolbar {
              flex-direction: column;
              align-items: stretch;
              padding: 10px;
              gap: 10px;
            }

            .cn-toolbar-group { width: 100%; }
            .cn-toolbar-label { width: 100%; }
            .cn-toolbar-spacer { display: none; }
            .cn-user-filter { width: 100%; }

            .cn-seg { width: 100%; }
            .cn-seg button { flex: 1; padding: 6px 4px; }

            .cn-link-btn { margin-left: 0; }

            .cn-board { gap: 10px 10px; }
            .cn-col { gap: 10px; }

            .cn-col-head {
              padding: 8px 10px;
              font-size: 11px;
              letter-spacing: 0.3px;
            }
            .cn-col-count { font-size: 11px; padding: 1px 7px; }

            .cn-cell > .cn-note { padding: 10px 10px !important; }

            .cn-note-header { font-size: 11px !important; gap: 5px !important; }
            .cn-note-header span { font-size: 10.5px !important; }
            .cn-note-header > span[style*="uppercase"],
            .cn-note-badge { padding: 1px 7px !important; font-size: 9.5px !important; }

            .cn-note-content,
            .cn-note-content * { font-size: 12.5px !important; line-height: 1.5 !important; }

            .cn-note-actions,
            .cn-note-actions * { font-size: 10.5px !important; }

            .cn-mention-chip { font-size: 10px !important; padding: 1px 6px !important; }
            .cn-mention-badge { font-size: 9.5px !important; padding: 2px 8px !important; }

            .cn-att-thumb { height: 140px !important; }

            .cn-empty-slot { font-size: 11px; min-height: 52px; }
          }

          /* ---- narrow tablets / big phones in landscape: still 2 columns ---- */
          @media (max-width: 640px) {
            .cn-board { gap: 8px 8px; }
            .cn-col { gap: 8px; }

            .cn-col-head { padding: 6px 8px; font-size: 10px; border-left-width: 3px; }
            .cn-col-title { gap: 4px; }
            .cn-col-title svg { width: 12px; height: 12px; }
            .cn-col-count { font-size: 10px; padding: 0 6px; }

            .cn-cell > .cn-note {
              padding: 8px 8px !important;
              border-left-width: 3px !important;
            }

            .cn-note-header { font-size: 10px !important; gap: 4px !important; margin-bottom: 4px !important; }
            .cn-note-header span { font-size: 9.5px !important; }
            .cn-note-header svg { width: 10px; height: 10px; }
            .cn-note-header > span[style*="uppercase"] {
              padding: 1px 5px !important;
              font-size: 8.5px !important;
              letter-spacing: 0.2px !important;
            }
            .cn-note-header label { margin-left: 0 !important; gap: 3px !important; font-size: 9.5px !important; }
            .cn-note-header input[type="checkbox"] { width: 11px; height: 11px; }

            .cn-note-content,
            .cn-note-content * { font-size: 11.5px !important; line-height: 1.45 !important; }

            .cn-note-actions { gap: 4px 6px !important; margin-top: 6px !important; padding-top: 6px !important; }
            .cn-note-actions,
            .cn-note-actions * { font-size: 9.5px !important; }
            .cn-note-actions button { padding: 2px 4px !important; }
            .cn-note-actions svg { width: 11px; height: 11px; }

            .cn-mention-chip { font-size: 9px !important; padding: 1px 5px !important; }
            .cn-mention-badge { font-size: 8.5px !important; padding: 1px 6px !important; margin-bottom: 6px !important; }

            .cn-att-thumb { height: 110px !important; }

            .cn-empty-slot { font-size: 10px; min-height: 44px; padding: 8px; }
          }

          /* ---- small windows ~500-560px wide: still 2 columns, compact ---- */
          @media (max-width: 560px) {
            .cn-cell > .cn-note { padding: 7px 7px !important; }

            .cn-note-header { font-size: 9.5px !important; }
            .cn-note-header span { font-size: 9px !important; }
            .cn-note-header > span[style*="uppercase"] { font-size: 8px !important; padding: 1px 4px !important; }

            .cn-note-content,
            .cn-note-content * { font-size: 11px !important; }

            .cn-note-actions,
            .cn-note-actions * { font-size: 9px !important; }

            .cn-mention-chip { font-size: 8.5px !important; }

            .cn-att-thumb { height: 90px !important; }
          }

          /* ---- phones: one note per row, label each note ---- */
          @media (max-width: 480px) {
            .cn-board,
            .cn-board.single {
              grid-template-columns: minmax(0, 1fr);
              gap: 10px;
            }

            .cn-board.both .cn-col-head { display: none; }
            .cn-board .cn-cell.is-empty { display: none; }

            .cn-board.both .cn-cell-tag {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              align-self: flex-start;
              font-size: 10px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.4px;
              padding: 3px 9px;
              border-radius: 6px 6px 0 0;
            }

            .cn-cell-tag.ext { background: #d1ecf1; color: #0c5460; }
            .cn-cell-tag.int { background: #fff3cd; color: #856404; }

            /* single column has room again -> a bit bigger than the 2-column sizes */
            .cn-cell > .cn-note { padding: 10px !important; }

            .cn-note-header { font-size: 10.5px !important; }
            .cn-note-header span { font-size: 10px !important; }
            .cn-note-header > span[style*="uppercase"] { font-size: 9px !important; padding: 1px 6px !important; }

            .cn-note-content,
            .cn-note-content * { font-size: 12.5px !important; }

            .cn-note-actions,
            .cn-note-actions * { font-size: 10px !important; }

            .cn-mention-chip { font-size: 9.5px !important; }

            .cn-att-thumb { height: 130px !important; }
          }

          /* ---- small phones ---- */
          @media (max-width: 380px) {
            .cn-seg-label-long { display: none; }

            .cn-cell > .cn-note { padding: 9px !important; }

            .cn-note-content,
            .cn-note-content * { font-size: 12px !important; }

            .cn-note-header span { font-size: 9.5px !important; }
          }
        `}
      </style>

      {/* ✅ CHANGED: prominent toast */}
      <NoteToast toast={toast} onClose={closeToast} />

      <div className="case-tabs-wrapper">
        <Tabs
          id="controlled-tab"
          activeKey={key}
          onSelect={(k) => {
            if (k) {
              setKey(k);
            }
          }}
          className="mb-3 custom-tabs"
          style={styles.tabs}
          mountOnEnter
          unmountOnExit={false}
        >
          <Tab
            eventKey="Details"
            title="Details"
            style={styles.tabPane}
          >
            <div style={styles.tabContent}>
              <CaseTable onProcedureUpdate={onProcedureUpdate} />
            </div>
          </Tab>

          <Tab
            eventKey="Notes"
            title={`Notes (${totalNotesCount})`}
            style={styles.tabPane}
          >
            <div
              className="notes-mobile"
              style={styles.notesContainer}
            >
              {isAdmin ? (
                <div style={styles.adminInfoBar}>
                  <FiShield size={14} />
                  <span>
                    Admin Mode: You can hide notes from all users.
                    Hidden notes are marked with red border.
                  </span>
                </div>
              ) : (
                <div style={styles.userInfoBar}>
                  <FiUser size={14} />
                  <span>
                    User Mode: You can only view notes visible to you.
                  </span>
                </div>
              )}

              <div style={styles.notesHeader}>
                <div style={styles.title}>
                  <SlNote size={18} color="#0066cc" />
                  Case Notes
                  <span style={styles.count}>
                    {totalNotesCount}
                  </span>
                </div>

                <button
                  style={styles.btnAddNote}
                  onClick={openModal}
                >
                  <FiPlus size={16} />
                  Add Note
                </button>
              </div>

              {isLoading && notes.length === 0 && !editorHtml ? (
                <div style={styles.loadingContainer}>
                  <Spinner animation="border" variant="primary" />
                  <p
                    style={{
                      marginTop: '12px',
                      color: '#6c757d',
                      fontSize: '14px'
                    }}
                  >
                    Loading notes...
                  </p>
                </div>
              ) : (
                <>
                  {editorHtml && (
                    <div style={styles.editorHtmlNote}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: safeEditorHtml
                        }}
                      />
                    </div>
                  )}

                  {notes.length === 0 ? (
                    !editorHtml && (
                      <div style={styles.emptyState}>
                        <FiFileText
                          size={24}
                          style={{
                            display: 'block',
                            margin: '0 auto 8px',
                            color: '#adb5bd'
                          }}
                        />
                        No notes yet
                      </div>
                    )
                  ) : (
                    <>
                      {/* ===== ✅ NEW: filter toolbar ===== */}
                      <div className="cn-toolbar">
                        <div className="cn-toolbar-group">
                          <span className="cn-toolbar-label">
                            <FiFilter size={12} /> User
                          </span>
                          <UserFilter
                            options={userOptions}
                            selected={selectedUser}
                            onSelect={handleSelectUser}
                          />
                        </div>

                        <div className="cn-toolbar-group">
                          <div className="cn-seg" role="group" aria-label="Filter type">
                            <button
                              type="button"
                              className={filterMode === 'all' ? 'is-on' : ''}
                              disabled={!selectedUser}
                              onClick={() => setFilterMode('all')}
                              title="Notes written by or mentioning this user"
                            >
                              <FiUsers size={12} /> All
                            </button>
                            <button
                              type="button"
                              className={filterMode === 'authored' ? 'is-on' : ''}
                              disabled={!selectedUser}
                              onClick={() => setFilterMode('authored')}
                              title="Notes written by this user"
                            >
                              <FiEdit3 size={12} /> Written
                            </button>
                            <button
                              type="button"
                              className={filterMode === 'mentioned' ? 'is-on' : ''}
                              disabled={!selectedUser}
                              onClick={() => setFilterMode('mentioned')}
                              title="Notes where this user is mentioned"
                            >
                              <FiAtSign size={12} /> Mentioned
                            </button>
                          </div>
                        </div>

                        <div className="cn-toolbar-spacer" />

                        <div className="cn-toolbar-group">
                          <span className="cn-toolbar-label">
                            <FiColumns size={12} /> View
                          </span>
                          <div className="cn-seg" role="group" aria-label="View">
                            <button
                              type="button"
                              className={viewMode === 'both' ? 'is-on' : ''}
                              onClick={() => setViewMode('both')}
                            >
                              Both
                              <span className="cn-seg-count">{filteredNotes.length}</span>
                            </button>
                            <button
                              type="button"
                              className={viewMode === 'external' ? 'is-on' : ''}
                              onClick={() => setViewMode('external')}
                            >
                              <FiGlobe size={12} />
                              <span className="cn-seg-label-long">External</span>
                              <span className="cn-seg-count">{externalNotes.length}</span>
                            </button>
                            <button
                              type="button"
                              className={viewMode === 'internal' ? 'is-on' : ''}
                              onClick={() => setViewMode('internal')}
                            >
                              <FiLock size={12} />
                              <span className="cn-seg-label-long">Internal</span>
                              <span className="cn-seg-count">{internalNotes.length}</span>
                            </button>
                          </div>
                        </div>

                        {selectedUser && (
                          <div className="cn-result">
                            <span
                              className="cn-avatar sm"
                              style={{ background: avatarColor(selectedUser.key) }}
                            >
                              {getInitials(selectedUser.name)}
                            </span>
                            <span>
                              Showing <strong>{filteredNotes.length}</strong> of{' '}
                              {notes.length} notes{' '}
                              {filterMode === 'authored'
                                ? 'written by'
                                : filterMode === 'mentioned'
                                ? 'mentioning'
                                : 'written by or mentioning'}{' '}
                              <strong>{selectedUser.name}</strong>
                            </span>
                            <button
                              type="button"
                              className="cn-link-btn"
                              onClick={clearFilter}
                            >
                              <FiX size={12} /> Clear filter
                            </button>
                          </div>
                        )}
                      </div>

                      {/* ===== ✅ NEW: External | Internal board ===== */}
                      {filteredNotes.length === 0 ? (
                        <div className="cn-filter-empty">
                          <FiSearch
                            size={24}
                            style={{
                              display: 'block',
                              margin: '0 auto 10px',
                              color: '#adb5bd'
                            }}
                          />
                          No notes found
                          {selectedUser ? ` for ${selectedUser.name}` : ''}.
                          <div style={{ marginTop: '10px' }}>
                            <button
                              type="button"
                              className="cn-link-btn"
                              style={{ margin: '0 auto' }}
                              onClick={clearFilter}
                            >
                              <FiX size={12} /> Clear filter
                            </button>
                          </div>
                        </div>
                      ) : (
                        renderBoard()
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </Tab>

          <Tab
            eventKey="Tasks"
            title="Tasks"
            disabled
            style={styles.tabPane}
          >
            <div style={styles.tabContent}>
              <Task />
            </div>
          </Tab>

          <Tab
            eventKey="Alert"
            title="Alerts"
            disabled
            style={styles.tabPane}
          >
            <div style={styles.tabContent}>
              <Alert setActiveTab={setKey} />
            </div>
          </Tab>
        </Tabs>
      </div>

      {/* ✅ NEW: in-app image viewer for note attachments */}
      {imageViewerNote && (
        <NoteImageViewer
          key={`${imageViewer.noteId}-${imageViewer.attId}`}
          note={imageViewerNote}
          startAttId={imageViewer.attId}
          onClose={closeImageViewer}
        />
      )}

      <AddNoteModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={addNote}
        isLoading={isLoading}
        defaultRecipient={defaultRecipient}
        editNote={editingNote}
        onUpdate={updateNote}
        initialFiles={prefillFiles}
        overlayZIndex={noteModalOnTop ? NOTE_MODAL_ON_TOP_Z : undefined}
      />
    </div>
  );
}

const styles = {
  container: {
    margin: 0,
    padding: 0,
    width: '100%',
    maxWidth: '100%'
  },

  toast: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 9999
  },

  tabs: {
    backgroundColor: '#f8f9fa',
    borderTop: '3px solid #0066cc',
    padding: 0,
    margin: 0,
    borderRadius: 0,
    width: '100%',
    maxWidth: '100%'
  },

  tabPane: {
    padding: 0,
    margin: 0,
    width: '100%',
    maxWidth: '100%'
  },

  tabContent: {
    padding: 0,
    margin: 0,
    width: '100%',
    maxWidth: '100%'
  },

  notesContainer: {
    backgroundColor: '#f8f9fa',
    padding: '24px',
    borderTop: '1px solid #e9ecef',
    margin: 0,
    width: '100%',
    maxWidth: '100%'
  },

  adminInfoBar: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  userInfoBar: {
    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    color: '#1a237e',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  notesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid #e9ecef'
  },

  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#212529'
  },

  count: {
    background: '#e9ecef',
    color: '#495057',
    padding: '0 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600
  },

  btnAddNote: {
    background: '#0066cc',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease'
  },

  loadingContainer: {
    textAlign: 'center',
    padding: '40px',
    background: 'white',
    borderRadius: '4px',
    border: '1px solid #e9ecef'
  },

  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: '#868e96',
    fontSize: '14px',
    border: '1px dashed #dee2e6',
    borderRadius: '4px'
  },

  addNoteFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '2px',
    paddingRight: '10px'
  },

  noteItem: {
    background: 'white',
    borderRadius: '4px',
    padding: '16px 20px',
    marginBottom: '8px',
    borderLeft: '3px solid #e9ecef',
    transition: 'all 0.2s ease',
    position: 'relative'
  },

  // ✅ NEW: subtle persistent ring for mentioned-you notes
  noteItemMentioned: {
    boxShadow: '0 0 0 2px #ffe08a, 0 4px 14px rgba(255, 193, 7, 0.25)'
  },

  // ✅ NEW: "Mentioned you" chip
  mentionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: '#ffc107',
    color: '#212529',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginBottom: '8px'
  },

  noteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
    fontSize: '13px',
    color: '#6c757d',
    flexWrap: 'wrap'
  },

  separator: {
    color: '#dee2e6'
  },

  noteContent: {
    fontSize: '14px',
    color: '#212529',
    lineHeight: '1.6',
    position: 'relative'
  },

  noteActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '8px 12px',
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #f1f3f5'
  },

  noteBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },

  internalBadge: {
    background: '#fff3cd',
    color: '#856404'
  },

  externalBadge: {
    background: '#d1ecf1',
    color: '#0c5460'
  },

  hiddenBadge: {
    background: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb'
  },

  adminBadge: {
    background: '#e8eaf6',
    color: '#283593'
  },

  adminOverlay: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: '#dc3545',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    zIndex: 10
  },

  // ✅ NEW: note editing UI
  actionBtnEdit: {
    background: '#eef4fb',
    border: '1px solid #cfe0fa',
    color: '#004aad',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '2px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  editCountdown: {
    background: '#004aad',
    color: '#fff',
    borderRadius: '8px',
    padding: '0 6px',
    fontSize: '10px',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    marginLeft: '2px'
  },

  editedTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '11px',
    fontStyle: 'italic',
    color: '#868e96'
  },

  editInfoBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    marginBottom: '14px',
    borderRadius: '6px',
    background: '#eef4fb',
    border: '1px solid #cfe0fa',
    color: '#004aad',
    fontSize: '12.5px',
    lineHeight: 1.4
  },

  editInfoBarExpired: {
    background: '#fff4f4',
    border: '1px solid #f0c4c4',
    color: '#c93636'
  },

  actionBtnAdmin: {
    background: 'none',
    border: 'none',
    color: '#dc3545',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '2px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  hiddenStatus: {
    fontSize: '11px',
    color: '#721c24',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  visibleStatus: {
    fontSize: '11px',
    color: '#283593',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },

  internalToggleCheckbox: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    marginLeft: '10px',
    userSelect: 'none'
  },

  hiddenInternalNote: {
    padding: '12px',
    background: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '6px',
    color: '#856404',
    textAlign: 'center',
    fontSize: '13px'
  },

  blurContent: {
    filter: 'blur(5px)',
    opacity: 0.3,
    transition: 'all 0.3s ease',
    pointerEvents: 'none',
    userSelect: 'none'
  },

  hiddenOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    zIndex: 5,
    padding: '20px'
  },

  editorHtmlNote: {
    background: 'white',
    borderRadius: '4px',
    padding: '16px 20px',
    marginBottom: '16px',
    borderLeft: '3px solid #17a2b8',
    position: 'relative'
  },

  modalContent: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    // ✅ CHANGED: size follows the screen (desktop 800px, tablets/foldables/phones shrink)
    width: 'min(800px, 94vw)',
    maxWidth: '94vw',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: 0,
    borderRadius: '10px',
    border: 'none',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)'
  },

  modalOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  },

  // ✅ CHANGED: #004aad header
  modalHeader: {
    padding: '16px 20px',
    background: '#004aad',
    backgroundImage: 'linear-gradient(115deg, #004aad 0%, #003b88 100%)',
    color: '#fff',
    borderRadius: '10px 10px 0 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexShrink: 0
  },

  modalHeaderIcon: {
    width: '34px',
    height: '34px',
    minWidth: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.14)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    borderRadius: '8px',
    color: '#fff'
  },

  modalTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 700,
    color: '#fff',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  modalSubtitle: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.75)',
    marginTop: '2px',
    letterSpacing: '0.2px'
  },

  modalCloseBtn: {
    background: 'rgba(255, 255, 255, 0.12)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    borderRadius: '8px',
    width: '32px',
    height: '32px',
    minWidth: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#fff',
    padding: 0,
    transition: 'background 0.15s ease'
  },

  // ✅ CHANGED: body scrolls inside the modal, header/footer stay visible
  modalBody: {
    padding: '20px 24px',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto'
  },

  modalFormRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px'
  },

  modalFormGroup: {
    flex: 1
  },

  modalLabel: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: 500,
    color: '#495057',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },

  modalInput: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s'
  },

  modalSelect: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none'
  },

  noteTypeToggle: {
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #e9ecef'
  },

  noteTypeLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontWeight: 500,
    color: '#495057',
    fontSize: '13px',
    margin: 0
  },

  /* outer wrapper: positioning context for the @ mention popup */
  editorOuter: {
    position: 'relative'
  },

  editorWrapper: {
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    overflow: 'hidden',
    background: 'white'
  },

  /* only used for the loading fallback */
  quillEditor: {
    height: '200px'
  },

  /* ===== in-editor previews (pasted images / files) ===== */
  previewStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '4px 14px 14px',
    background: 'white',
    maxHeight: '220px',
    overflowY: 'auto'
  },

  previewItem: {
    position: 'relative',
    display: 'inline-flex',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    background: 'white',
    overflow: 'hidden'
  },

  previewImg: {
    display: 'block',
    maxWidth: '260px',
    maxHeight: '160px',
    objectFit: 'contain'
  },

  previewFileBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 36px 10px 12px',
    maxWidth: '300px'
  },

  previewFileName: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#212529',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  previewFileSize: {
    fontSize: '11px',
    color: '#868e96',
    flex: '0 0 auto'
  },

  previewRemove: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(33, 37, 41, 0.75)',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    padding: 0
  },

  /* footer strip inside the editor box */
  editorFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 10px',
    borderTop: '1px solid #e9ecef',
    background: '#f8f9fa'
  },

  editorFooterBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    color: '#495057',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px'
  },

  editorFooterHint: {
    fontSize: '11px',
    color: '#868e96'
  },

  /* @ mention popup */
  mentionPopover: {
    position: 'absolute',
    zIndex: 30,
    width: '280px',
    maxWidth: 'calc(100% - 8px)',
    background: 'white',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    padding: '8px'
  },

  mentionPopoverTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginBottom: '6px'
  },

  mentionPopoverList: {
    maxHeight: '260px',
    overflowY: 'auto'
  },

  // ✅ CHANGED: #004aad footer
  modalFooter: {
    padding: '14px 20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    backgroundColor: '#004aad',
    backgroundImage: 'linear-gradient(115deg, #004aad 0%, #003b88 100%)',
    borderRadius: '0 0 10px 10px',
    flexShrink: 0
  },

  modalCancelBtn: {
    backgroundColor: 'transparent',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.55)',
    padding: '8px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.2s ease'
  },

  modalSaveBtn: {
    backgroundColor: '#fff',
    color: '#004aad',
    border: '1px solid #fff',
    padding: '8px 24px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 700,
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '110px'
  },

  /* ===== Attachment UI styles (shown on saved notes) ===== */
  attachmentsWrap: {
    marginTop: '12px',
    borderTop: '1px dashed #e9ecef',
    paddingTop: '10px'
  },

  attachmentsLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#495057',
    marginBottom: '8px'
  },

  // ✅ min(100%, ...) so cards never overflow a narrow (half-width) column
  attachmentsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
    gap: '10px'
  },

  attachmentCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '6px 8px',
    textDecoration: 'none',
    minWidth: 0
  },

  /* large image preview card */
  attachmentCardImage: {
    display: 'flex',
    flexDirection: 'column',
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    overflow: 'hidden',
    minWidth: 0
  },

  attachmentThumbLinkLarge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    background: '#f1f3f5',
    cursor: 'zoom-in'
  },

  attachmentThumbLarge: {
    width: '100%',
    height: '200px',
    objectFit: 'contain',
    display: 'block'
  },

  attachmentImageFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderTop: '1px solid #e9ecef',
    background: '#fff'
  },

  attachmentThumbLink: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  attachmentThumb: {
    width: '38px',
    height: '38px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
    background: '#fff'
  },

  attachmentIconBox: {
    width: '38px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#eef4fb',
    borderRadius: '4px',
    flex: '0 0 auto'
  },

  attachmentInfo: {
    flex: 1,
    minWidth: 0
  },

  attachmentName: {
    fontSize: '12px',
    color: '#212529',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  attachmentMeta: {
    fontSize: '11px',
    color: '#868e96'
  },

  attachmentDownload: {
    color: '#0066cc',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '4px',
    textDecoration: 'none'
  }
};

export default React.memo(CaseTab);